import axios from 'axios';
import Cookies from 'js-cookie';
import { SNIPPET_COOKIE_NAMES, isSnippetContext, isSnippetPage, portalCookieOpts } from '@/utils/authCookies';
import { EMBED_EVENTS, notifyParent } from '@/utils/embedBridge';
import store from '../store';
import router from '../router';
import Swal from 'sweetalert2';
import logoutImage from '@/assets/images/logout.png';

// backend ตอบ HTTP 200 + message นี้ (Messsage.TokenExpired) เมื่อ Bearer token หมดอายุหรืออ่านไม่ได้
export const TOKEN_EXPIRED_MESSAGE = 'Token has expired!!!';
// helper ที่ส่ง Bearer token ยิง event นี้เมื่อเจอ response ข้างบน — หน้าแชท (FullStreamReDesignV2) ฟังแล้วจัดการที่จุดเดียว
// หน้าอื่นไม่ได้ฟัง event นี้ พฤติกรรมเดิมไม่เปลี่ยน
export const TOKEN_EXPIRED_EVENT = 'fusion:token-expired';

const APICall = {
  install(app) {
    app.config.globalProperties.$call = {
      urlApi: import.meta.env.VITE_APP_API,
      //ChackPersonalInfo(callback, error) {
      //  var request = null;
      //  this.apiPostLogin('api/v0/SSO/ChackPersonalInfo', request, callback, error);
      //},
      Login(request, callback, error) {
        this.apiPostLogin('api/v0/Login/Login', request, callback, error);
      },
      LoginFullStream(request, callback, error) {
        // ใช้ apiPost (ไม่ใช่ apiPostLogin) เพื่อเลี่ยง legacy cookie/store mutations + ได้ withCredentials:true
        // session cookie จาก SignInAsync จะถูก browser save อัตโนมัติ; user state hydrate ผ่าน fetchUser หลังจากนี้
        this.apiPost('api/v0/Login/FullStreamLogin', request, callback, error);
      },
      LoginThaiIDAuthenticate(request, callback, error) {
        this.apiPostLogin('api/v0/LoginThaiID/authenticate', request, callback, error);
      },
      LoginThaiIDSwitchCompany(callback, error) {
        this.apiPostAuth('api/v0/LoginThaiID/switch-company', null, callback, error);
      },
      GetDigitalIdentityStatus(callback, error) {
        this.apiGetAuth('api/v0/LoginThaiID/link-status', callback, error);
      },
      GetDigitalIdentityStatusNoAlert(callback, error) {
        this.apiGetAuthNoAlert('api/v0/LoginThaiID/link-status', callback, error);
      },
      UnlinkThaiID(callback, error) {
        this.apiDeleteAuth('api/v0/LoginThaiID/unlink', callback, error);
      },
      // ยกเลิกผูก Digital ID — ไม่เหลือทางเข้าอื่น backend ปิดบัญชีแล้วตอบ data = 'ACCOUNT_CLOSED' (ผู้เรียกต้อง logout ต่อ)
      UnlinkDGADigitalID(callback, error) {
        this.apiDeleteAuth('api/v0/LoginDGADigitalID/unlink', callback, error);
      },
      SuperAdminSwitchCompany(request, callback, error) {
        this.apiPostAuth('api/v0/Login/SwitchCompany', request, callback, error);
      },
      SuperAdminResetToSuperAdmin(callback, error) {
        this.apiPostAuth('api/v0/Login/ResetToSuperAdmin', null, callback, error);
      },

      // ====================== NEXA Knowledge Graphs (proxy + iframe) ======================
      ListKnowledgeGraphs(callback, error) {
        this.apiPostAuth('api/v0/KnowledgeGraph/List', null, callback, error);
      },
      GetKnowledgeGraphById(graphId, callback, error) {
        this.apiPostAuth('api/v0/KnowledgeGraph/GetById', { GraphId: graphId }, callback, error);
      },
      CreateKnowledgeGraph(request, callback, error) {
        this.apiPostAuth('api/v0/KnowledgeGraph/Create', request, callback, error);
      },
      UpdateKnowledgeGraph(request, callback, error) {
        this.apiPostAuth('api/v0/KnowledgeGraph/Update', request, callback, error);
      },
      DeleteKnowledgeGraph(graphId, callback, error) {
        this.apiPostAuth('api/v0/KnowledgeGraph/Delete', { GraphId: graphId }, callback, error);
      },
      GetKnowledgeGraphEmbedToken(callback, error) {
        this.apiPostAuth('api/v0/KnowledgeGraph/GetEmbedToken', null, callback, error);
      },
      // Preview a pdf/image link from a chat answer. fileUrl is our own Asset
      // proxy URL (built by MarkedMarkdown), which resolves the path against
      // NexaBaseUrl from sy.Dbsettings. Fetched as a blob rather than used as an
      // <img>/<iframe> src so it carries the auth cookie in dev (SPA and API sit
      // on different origins there) and so an expired link surfaces as an error
      // instead of a broken frame.
      GetKnowledgeGraphAsset(fileUrl, callback, error) {
        const url = String(fileUrl || '');
        if (!url.startsWith('api/v0/KnowledgeGraph/Asset?')) {
          return error(new Error('Unexpected file preview URL'));
        }
        this.apiGetBlobAuthData(url, callback, error);
      },

      // Invitation
      SendInvitation(request, callback, error) {
        this.apiPostAuth('api/v0/Invitation/send', request, callback, error);
      },
      GetInvitationList(callback, error) {
        this.apiGetAuth('api/v0/Invitation/list', callback, error);
      },
      SAGetInvitationList(callback, error) {
        this.apiGetAuth('api/v0/Invitation/list-superadmin', callback, error);
      },
      ResendInvitation(id, callback, error) {
        this.apiPostAuth(`api/v0/Invitation/resend?id=${id}`, null, callback, error);
      },
      RevokeInvitation(id, callback, error) {
        this.apiDeleteAuth(`api/v0/Invitation/revoke?id=${id}`, callback, error);
      },
      ValidateInvitation(token, callback, error) {
        this.apiGet(`api/v0/Invitation/validate?token=${encodeURIComponent(token)}`, callback, error);
      },
      AutoLogin(request, callback, error) {
        this.apiPostAuth('api/v0/Login/AutoLogin', request, callback, error);
      },
      ForgetPassword(param, callback, error) {
        this.apiGetAuth(`api/v0/Login/ForgetPassword?email=${param}`, callback, error);
      },
      ChatLogin(request, callback, error) {
        this.apiPostLoginChat('api/v0/Login/ChatLogin', request, callback, error);
      },
      RegisterSnippetUser(request, callback, error) {
        // Public chat register — backend insert sy.User (roleId=4, IsUserChat=true) + auto sign-in cookie.
        // Response shape เหมือน FullStreamLogin (UserResponse) ดังนั้นใช้ apiPost (withCredentials) ตามเดียวกัน
        this.apiPost('api/v0/Login/RegisterFullStreamPublic', request, callback, error);
      },
      CheckStatusSSO(request, callback, error) {
        // Public SSO status endpoint: avoid auth-helper 401 redirect side effects on /login and /register.
        this.apiPost('api/v0/SSO/CheckStatusSSO', request, callback, error);
      },
      SnippetSsoExchange(request, callback, error) {
        // แลก one-time SSO exchange code → snippet chat token จริง (cross-domain snippet อ่าน cookie ข้าม domain ไม่ได้)
        // ใช้ apiPost (ไม่มี Bearer — ยังไม่มี token; code คือตัวพิสูจน์) ผ่าน CORS AllowedOrigin
        // provider: 'thaid' → LoginThaiID, 'session' → SnippetSession (กลับจากแท็บ Landing), อื่นๆ (digitalid) → LoginDGADigitalID
        const provider = (request?.provider ? request.provider : '').toLowerCase();
        const paths = {
          thaid: 'api/v0/LoginThaiID/snippet-exchange',
          session: 'api/v0/SnippetSession/exchange',
        };
        const path = paths[provider] || 'api/v0/LoginDGADigitalID/snippet-exchange';
        this.apiPost(path, { code: request.code }, callback, error);
      },
      CreateSnippetReturn(request, token, callback, error) {
        // URL หน้าเว็บลูกค้า (snippet domain) สำหรับปุ่ม "เริ่มใช้งาน" บน Landing — ส่ง Bearer ของ widget เมื่อ login อยู่
        // เพื่อให้ server แนบ one-time code ที่พก session ไปด้วย; ไม่มี token = URL เปล่า (anonymous)
        if (token) {
          this.apiPostToken('api/v0/SnippetSession/create-return', request, callback, error, token);
        } else {
          this.apiPost('api/v0/SnippetSession/create-return', request, callback, error);
        }
      },
      loginMicrosoft(callback, error) {
        this.apiGetAuth('api/v0/SSO/SignInChat', callback, error);
      },
      GetUserProfile(callback, error) {
        let request = null;
        this.apiPostAuth('api/v0/SSO/GetUserProfile', request, callback, error);
      },
      GetUserChatProfile(callback, error) {
        let request = null;
        this.apiPostAuth('api/v0/SSO/GetUserChatProfile', request, callback, error);
      },
      SaveAgreeConsent(request, callback, error) {
        const body = { targetSystem: 'backend', ...request };
        this.apiPostAuth('api/v0/User/SaveAgreeConsent', body, callback, error);
      },

      //UserManagement
      GetTableUserManagement(request, callback, error) {
        this.apiPostAuth('api/v0/User/GetTableUserManagement', request, callback, error);
      },
      CreateUser(request, callback, error) {
        this.apiPostAuth('api/v0/User/CreateUser', request, callback, error);
      },
      UpdateUser(request, callback, error) {
        this.apiPostAuth('api/v0/User/UpdateUser', request, callback, error);
      },
      UpdateUserProfile(request, callback, error) {
        this.apiPostAuth('api/v0/User/UpdateUserProfile', request, callback, error);
      },
      DeleteUser(param, callback, error) {
        this.apiDeleteAuth('api/v0/User/DeleteUser?id=' + param, callback, error);
      },
      SaveChangePassword(request, callback, error) {
        this.apiPostAuth('api/v0/User/SaveChangePassword', request, callback, error);
      },
      ChangePassword(request, callback, error) {
        this.apiPostAuth('api/v0/User/ChangePassword', request, callback, error);
      },
      GetTableUserChatManagement(request, callback, error) {
        this.apiPostAuth('api/v0/UserChat/GetTableUserManagement', request, callback, error);
      },
      CreateUserChat(request, callback, error) {
        this.apiPostAuth('api/v0/UserChat/CreateUser', request, callback, error);
      },
      UpdateUserChat(request, callback, error) {
        this.apiPostAuth('api/v0/UserChat/UpdateUser', request, callback, error);
      },
      DeleteUserChat(param, callback, error) {
        this.apiDeleteAuth('api/v0/UserChat/DeleteUser?id=' + param, callback, error);
      },
      SaveChangePasswordUserChat(request, callback, error) {
        this.apiPostAuth('api/v0/UserChat/SaveChangePasswordUserChat', request, callback, error);
      },

      // Self-service profile endpoints for chat users (roleId=4) — safe equivalents of /api/v0/User/*
      GetMyChatProfile(callback, error) {
        this.apiGetAuth('api/v0/UserChat/GetMyProfile', callback, error);
      },
      UpdateMyChatProfile(request, callback, error) {
        this.apiPostAuth('api/v0/UserChat/UpdateMyProfile', request, callback, error);
      },
      ChangeMyChatPassword(request, callback, error) {
        this.apiPostAuth('api/v0/UserChat/ChangeMyPassword', request, callback, error);
      },
      ChatRequestEnableTwoFactor(callback, error) {
        this.apiPostAuth('api/v0/UserChat/RequestEnableTwoFactor', null, callback, error);
      },
      ChatConfirmEnableTwoFactor(request, callback, error) {
        this.apiPostAuth('api/v0/UserChat/ConfirmEnableTwoFactor', request, callback, error);
      },
      ChatDisableTwoFactor(callback, error) {
        this.apiPostAuth('api/v0/UserChat/DisableTwoFactor', null, callback, error);
      },
      GetVerifiedIdentityChat(callback, error) {
        this.apiGetAuth('api/v0/UserChat/GetVerifiedIdentity', callback, error);
      },

      DownloadTemplateUser(request, callback, error) {
        this.apiPostTypeFileAuth('api/v0/User/DownloadTemplateUser', request, callback, error);
      },
      ImportUser(request, Config, callback, error) {
        this.apiPostImportFileAuth(`api/v0/User/ImportUser?isUserChat=${request.isUserChat}`, request.entity, Config, callback, error);
      },
      SyncUserChat(request, callback, error) {
        this.apiPostAuth('api/v0/SSO/SyncUserAzure', request, callback, error);
      },

      //BlacklistChatUser
      GetTableBlacklistChatUserManagement(request, callback, error) {
        this.apiPostAuth('api/v0/BlacklistChatUser/GetTableBlacklistChatUserManagement', request, callback, error);
      },
      GetLineUserID(callback, error) {
        this.apiGetAuth('api/v0/BlacklistChatUser/GetLineUserID', callback, error);
      },
      SetBlacklistChatUser(request, callback, error) {
        this.apiPostAuth('api/v0/BlacklistChatUser/SetBlacklistChatUser', request, callback, error);
      },
      DeleteBlacklistChatUser(request, callback, error) {
        this.apiPostAuth('api/v0/BlacklistChatUser/DeleteBlacklistChatUser', request, callback, error);
      },

      //GPTModel Management
      GetTableGPTModel(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModel/GetTableGPTModel', request, callback, error);
      },
      GetTableGPTModelVersioning(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModel/GetTableGPTModelVersioning', request, callback, error);
      },
      GetGPTModelById(param, callback, error) {
        this.apiGetAuth(`api/v0/ManageGPTModel/GetGPTModelById?id=${param}`, callback, error);
      },
      PublishGPTModel(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModel/PublishGPTModel', request, callback, error);
      },
      ReplaceGPTModel(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModel/ReplaceGPTModel', request, callback, error);
      },
      AddGPTModel(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModel/AddGPTModel', request, callback, error);
      },
      UpdateGPTModel(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModel/UpdateGPTModel', request, callback, error);
      },
      DeleteGPTModel(param, callback, error) {
        this.apiDeleteAuth('api/v0/ManageGPTModel/DeleteGPTModel?id=' + param, callback, error);
      },
      DDGPTModel(callback, error) {
        this.apiGetAuth('api/v0/ManageGPTModel/DDGPTModel', callback, error);
      },
      // แสดงทุกโมเดล (ไม่กรองด้วย LLM group) — runtime ยัง fallback ไป default เมื่อไม่มีสิทธิ์
      DDGPTModelAll(callback, error) {
        this.apiGetAuth('api/v0/ManageGPTModel/DDGPTModel_All', callback, error);
      },
      DDGPTModelIsPublish(callback, error) {
        this.apiGetAuth('api/v0/ManageGPTModel/DDGPTModel_IsPublish', callback, error);
      },
      // แสดงทุกโมเดลที่ publish (ไม่กรองด้วย LLM group) — runtime ยัง fallback ไป default เมื่อไม่มีสิทธิ์
      DDGPTModelIsPublishAll(callback, error) {
        this.apiGetAuth('api/v0/ManageGPTModel/DDGPTModel_IsPublish_All', callback, error);
      },
      DDGPTModel_IsPublishByUser(callback, error) {
        this.apiGetAuth('api/v0/ManageGPTModel/DDGPTModel_IsPublishByUser', callback, error);
      },
      GetAllDDModelManagement(id, callback, error) {
        this.apiGetAuth('api/v0/ManageGPTModel/GetDataForConfigModel?id=' + id, callback, error);
      },
      // เหมือน GetAllDDModelManagement แต่ modelData แสดงทุกโมเดล (ไม่กรองด้วย LLM group) — ใช้กับหน้า Flow
      GetAllDDModelManagementAll(id, callback, error) {
        this.apiGetAuth('api/v0/ManageGPTModel/GetDataForConfigModel?id=' + id + '&all=true', callback, error);
      },
      GetDDLModelDataAzureProvider(callback, error) {
        this.apiGetAuth('api/v0/ManageGPTModel/GetDDLModelDataAzureProvider', callback, error);
      },
      ExportFileConfigModel(request, callback, error) {
        this.apiPostTypeFileAuth(`api/v0/ManageGPTModel/ExportFileConfigModel`, request, callback, error);
      },
      ImportFileModelConfig(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModel/ImportFileModelConfig', request, callback, error);
      },

      //GPTModelNode Management
      GetTableGPTModelNode(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/GetTableGPTModelNode', request, callback, error);
      },
      GetGPTModelNodeById(param, callback, error) {
        this.apiGetAuth(`api/v0/ManageGPTModelNode/GetGPTModelNodeById?id=${param}`, callback, error);
      },
      AddGPTModelNode(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/AddGPTModelNode', request, callback, error);
      },
      UpdateGPTModelNode(request, callback, error) {
        this.apiPutAuth('api/v0/ManageGPTModelNode/UpdateGPTModelNode', request, callback, error);
      },
      DeleteGPTModelNode(param, callback, error) {
        this.apiDeleteAuth('api/v0/ManageGPTModelNode/DeleteGPTModelNode?id=' + param, callback, error);
      },
      GetDDLVector(callback, error) {
        this.apiGetAuth(`api/v0/ManageGPTModelNode/GetDDLVector`, callback, error);
      },
      GetDDVectorDBNameManagement(callback, error) {
        this.apiGetAuth(`api/v0/ManageGPTModelNode/GetDDVectorDBNameManagement`, callback, error);
      },

      //Upload CSV
      GetMaxLimitedCharacter(callback, error) {
        this.apiGetAuth(`api/v0/Chat/GetMaxLimitedCharacter`, callback, error);
      },
      GetMaxLimitedCharacter_FullStream(Token, callback, error) {
        this.apiGetToken(`api/v0/Chat/GetMaxLimitedCharacterFullStream`, callback, error, Token);
      },
      UploadCSV2Vector(request, callback, error) {
        let url = `api/v0/Chat/UploadFile?modeltype=${request.modelType}&vectordbname=${request.vectordbName}&deleteAll=${request.deleteAll}&DetectLanguage=${request.DetectLanguage}&MaxLimitedCharacter=${request.MaxLimitedCharacter}`;
        if (request.customCharacter != null) {
          url += `&customCharacter=${request.customCharacter}`;
        }
        if (request.groupReplaceID != null) {
          url += `&groupReplaceID=${request.groupReplaceID}`;
        }
        if (request.EventCreate != null) {
          url += `&EventCreate=${request.EventCreate}`;
        }
        this.apiPostTypeFileNoResponseAuth(url, request.entity, callback, error);
      },
      UploadCSV2Vector2(request, callback, error, Token) {
        let url = `api/v0/Chat/UploadFile?modeltype=${request.modelType}&vectordbname=${request.vectordbName}&deleteAll=${request.deleteAll}&DetectLanguage=${request.DetectLanguage}&MaxLimitedCharacter=${request.MaxLimitedCharacter}`;
        if (request.customCharacter != null) {
          url += `&customCharacter=${request.customCharacter}`;
        }
        if (request.groupReplaceID != null) {
          url += `&groupReplaceID=${request.groupReplaceID}`;
        }
        this.apiPostTypeFileNoResponseAuth(url, request.entity, callback, error, Token);
      },
      SaveEditVector(request, callback, error) {
        this.apiPostAuth(`api/v0/Chat/SaveEditVector`, request, callback, error);
      },
      SaveAddVector(request, callback, error) {
        this.apiPostAuth(`api/v0/Chat/SaveAddVector`, request, callback, error);
      },
      VectorOnOff(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/VectorOnOff', request, callback, error);
      },
      UploadCSVAnswers(request, callback, error) {
        this.apiPostAuth('api/v0/Chat/UploadCSVAnswers', request, callback, error);
      },
      GetVectorDBName(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/GetVectorDBName', request, callback, error);
      },
      GetVectorDBNameByName(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/GetVectorDBNameByName', request, callback, error);
      },
      GetViewVectorDBNameByName(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/GetViewVectorDBNameByName', request, callback, error);
      },
      GetPrivateVectorDBNameByName(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/GetPrivateVectorDBNameByName', request, callback, error);
      },
      DeleteVectorDBNameByName(param, callback, error) {
        this.apiDeleteAuth(`api/v0/ManageGPTModelNode/DeleteVectorDBNameByName?vectorDBName=${param}`, callback, error);
      },
      UpdatePersonalInformation(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/UpdatePersonalInformation', request, callback, error);
      },
      // Document Version Cycle — ตั้งค่าประทับวันที่เวอร์ชันรายเอกสาร (Admin หน่วยงาน) { vectorDBName, versionStampEnabled: true|false|null } → data: bool
      UpdateVersionStamp(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/UpdateVersionStamp', request, callback, error);
      },
      DeleteVectorDBNameById(param, callback, error) {
        this.apiDeleteAuth(`api/v0/ManageGPTModelNode/DeleteVectorDBNameById?id=${param}`, callback, error);
      },
      ReProcessedVector(request, callback, error) {
        this.apiPostAuth('api/v0/Chat/ReProcessed', request, callback, error);
      },
      ReFreshVector(request, callback, error) {
        this.apiPostAuth('api/v0/Chat/ReFreshVector', request, callback, error);
      },
      ReFreshViewVector(request, callback, error) {
        this.apiPostAuth('api/v0/Chat/ReFreshViewVector', request, callback, error);
      },
      ReFreshPrivateVector_Stream(request, callback, error) {
        this.apiPostAuth('api/v0/Chat/ReFreshPrivateVectorStream', request, callback, error);
      },
      ReFreshPrivateVector_FullStream(request, Token, callback, error) {
        this.apiPostToken('api/v0/Chat/ReFreshPrivateVectorFullStream', request, callback, error, Token);
      },
      GetTableVector(request, callback, error) {
        this.apiPostAuth('api/v0/Chat/GetTableVector', request, callback, error);
      },
      GetVectorCSVExport(param, callback, error) {
        this.apiGetAuth(`api/v0/Chat/GetVectorCSVExport?vectordbname=${param}`, callback, error);
      },
      GetVectorExcelExport(param, callback, error) {
        this.apiGetBlobAuth(`api/v0/Chat/GetVectorExcelExport?vectordbname=${param}`, callback, error);
      },
      DownloadTemplateVector_Stream(request, callback, error) {
        this.apiPostTypeFileAuth(`api/v0/Chat/DownloadTemplateVectorStream`, request, callback, error);
      },
      DownloadTemplateVector_FullStream(request, Token, callback, error) {
        this.apiPostTypeFileToken(`api/v0/Chat/DownloadTemplateVectorFullStream`, request, callback, error, Token);
      },
      DownloadUserManual(callback, error) {
        this.apiGetAuth(`api/v0/Chat/DownloadUserManual`, callback, error);
      },
      SaveDuplicateVector(request, callback, error) {
        this.apiPostAuth('api/v0/Chat/SaveDuplicateVector', request, callback, error);
      },
      SaveReplaceVector(request, callback, error) {
        this.apiPostAuth('api/v0/Chat/SaveReplaceVector', request, callback, error);
      },
      GetTableVectorVersion(request, callback, error) {
        this.apiPostAuth('api/v0/Chat/GetTableVectorVersion', request, callback, error);
      },
      SaveReStoreVector(request, callback, error) {
        this.apiPostAuth('api/v0/Chat/SaveReStoreVector', request, callback, error);
      },
      // ===== KM cross-company sharing =====
      GetShareableChildCompanies(ownerCompanyId, callback, error) {
        let url = 'api/v0/ManageGPTModelNode/GetShareableChildCompanies';
        if (ownerCompanyId != null) {
          url += `?ownerCompanyId=${ownerCompanyId}`;
        }
        this.apiGetAuth(url, callback, error);
      },
      GetKMShareTargets(vectorDBName, callback, error) {
        this.apiGetAuth(`api/v0/ManageGPTModelNode/GetKMShareTargets?vectorDBName=${encodeURIComponent(vectorDBName)}`, callback, error);
      },
      GetKMAccess(vectorDBName, callback, error) {
        this.apiGetAuth(`api/v0/ManageGPTModelNode/GetKMAccess?vectorDBName=${encodeURIComponent(vectorDBName)}`, callback, error);
      },
      SaveKMShare(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/SaveKMShare', request, callback, error);
      },
      RevokeKMShare(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/RevokeKMShare', request, callback, error);
      },
      // เจ้าของปรับสิทธิ์ (Edit/Delete) ของบริษัทที่แชร์ให้ ใน dialog จัดการการแชร์ (ไม่เปลี่ยนสถานะยอมรับ)
      UpdateKMSharePermission(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/UpdateKMSharePermission', request, callback, error);
      },
      // ===== KM cross-company sharing v2 — key-based + acceptance flow =====
      GenerateCompanyKey(callback, error) {
        this.apiPostAuth('api/v0/CompanyShareKey/GenerateKey', null, callback, error);
      },
      GetMyCompanyKey(callback, error) {
        this.apiGetAuth('api/v0/CompanyShareKey/GetMyActiveKey', callback, error);
      },
      ValidateCompanyKey(key, callback, error) {
        this.apiGetAuth(`api/v0/CompanyShareKey/ValidateKey?key=${encodeURIComponent(key)}`, callback, error);
      },
      SaveKMShareByKey(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/SaveKMShareByKey', request, callback, error);
      },
      AcceptKMShare(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/AcceptKMShare', request, callback, error);
      },
      RejectKMShare(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/RejectKMShare', request, callback, error);
      },
      UploadCSV2Vector_Stream_Old(request, callback, error) {
        let url = `api/v0/Chat/UploadFilePrivateStream?modeltype=${request.modelType}&vectordbname=${request.vectordbName}&deleteAll=${request.deleteAll}&DetectLanguage=${request.DetectLanguage}&USER_CHANNEL=${request.USER_CHANNEL}&TopVDB=${request.topVDB}&FileName=${request.searchVectorDBName2}&FileId=${request.fileId}&cosine=${request.cosine}`;
        if (request.customCharacter != null) {
          url += `&customCharacter=${request.customCharacter}`;
        }
        if (request.groupReplaceID != null) {
          url += `&groupReplaceID=${request.groupReplaceID}`;
        }
        if (request.MaxLimitedCharacter != null) {
          url += `&MaxLimitedCharacter=${request.MaxLimitedCharacter}`;
        }
        this.apiPostTypeFileNoResponseAuth(url, request.entity, callback, error);
      },
      UploadCSV2Vector_Stream(request, callback, error) {
        let url = `api/v0/Chat/UploadFilePrivateStream`;
        this.apiPostTypeFileMultipart(url, request, callback, error);
      },
      UploadCSV2Vector_FullStream(request, Token, callback, error) {
        let url = `api/v0/Chat/UploadFilePrivateFullStream?modeltype=${request.modelType}&vectordbname=${request.vectordbName}&deleteAll=${request.deleteAll}&DetectLanguage=${request.DetectLanguage}&USER_CHANNEL=${request.USER_CHANNEL}&TopVDB=${request.topVDB}&FileName=${request.searchVectorDBName2}&FileId=${request.fileId}&cosine=${request.cosine}`;
        if (request.customCharacter != null) {
          url += `&customCharacter=${request.customCharacter}`;
        }
        if (request.groupReplaceID != null) {
          url += `&groupReplaceID=${request.groupReplaceID}`;
        }
        if (request.MaxLimitedCharacter != null) {
          url += `&MaxLimitedCharacter=${request.MaxLimitedCharacter}`;
        }
        this.apiPostTypeFileNoResponseToken(url, request.entity, callback, error, Token);
      },
      ReProcessedVector_Stream(request, callback, error) {
        this.apiPostAuth('api/v0/Chat/ReProcessedPrivateStream', request, callback, error);
      },
      ReProcessedVector_FullStream(request, Token, callback, error) {
        this.apiPostToken('api/v0/Chat/ReProcessedPrivateFullStream', request, callback, error, Token);
      },
      GetTablePrivateVectorVersion_Stream(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStream/GetTablePrivateVectorVersionStream', request, callback, error);
      },
      GetTablePrivateVectorVersion_FullStream(request, Token, callback, error) {
        this.apiPostToken('api/v0/GPTStream/GetTablePrivateVectorVersionFullStream', request, callback, error, Token);
      },
      SaveReStorePrivateVector_Stream(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStream/SaveReStorePrivateVectorStream', request, callback, error);
      },
      SaveReStorePrivateVector_FullStream(request, Token, callback, error) {
        this.apiPostToken('api/v0/GPTStream/SaveReStorePrivateVectorFullStream', request, callback, error, Token);
      },
      PrivateVectorOnOff_Stream(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStream/PrivateVectorOnOffStream', request, callback, error);
      },
      PrivateVectorOnOff_FullStream(request, Token, callback, error) {
        this.apiPostToken('api/v0/GPTStream/PrivateVectorOnOffFullStream', request, callback, error, Token);
      },
      PrivateVectorOnOffByFileID_Stream(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStream/PrivateVectorOnOffByFileIDStream', request, callback, error);
      },
      PrivateVectorOnOffByFileID_FullStream(request, Token, callback, error) {
        this.apiPostToken('api/v0/GPTStream/PrivateVectorOnOffByFileIDFullStream', request, callback, error, Token);
      },

      //DataIntegration
      GetDDdataintegration(callback, error) {
        this.apiGetAuth(`api/v0/DataIntegration/GetDDdataintegration`, callback, error);
      },
      GetTableDataIntegration(request, callback, error) {
        this.apiPostAuth(`api/v0/DataIntegration/GetTableDataIntegration`, request, callback, error);
      },
      GetDataIntegrationDetail(id, callback, error) {
        this.apiGetAuth(`api/v0/DataIntegration/GetDataIntegrationDetail?id=${id}`, callback, error);
      },
      AddDataIntegration_Table(request, callback, error) {
        this.apiPostAuth(`api/v0/DataIntegration/AddDataIntegration_Table`, request.ReqData, callback, error);
      },
      AddDataIntegration_Data(request, callback, error) {
        this.apiPostTypeFileNoResponseAuth(`api/v0/DataIntegration/AddDataIntegration_Data?id=${request.id}&deleteAll=${request.deleteAll}`, request.uploaded_file, callback, error);
      },
      UpdateDataIntegration(request, callback, error) {
        this.apiPostAuth(`api/v0/DataIntegration/UpdateDataIntegration`, request, callback, error);
      },
      DeleteDataIntegration(id, callback, error) {
        this.apiDeleteAuth(`api/v0/DataIntegration/DeleteDataIntegration?id=${id}`, callback, error);
      },
      GetDDLDataIntegration(callback, error) {
        this.apiGetAuth(`api/v0/DataIntegration/GetDDLDataIntegration`, callback, error);
      },
      TestConnection(request, callback, error) {
        this.apiPostAuth(`api/v0/DataIntegration/TestConnection`, request, callback, error);
      },
      ValidateExternalColumn(request, callback, error) {
        this.apiPostAuth(`api/v0/DataIntegration/ValidateExternalColumn`, request, callback, error);
      },
      AddDataIntegrationConnection(request, callback, error) {
        this.apiPostAuth(`api/v0/DataIntegration/AddDataIntegrationConnection`, request, callback, error);
      },
      GetTableDataIntegrationDetail(request, callback, error) {
        this.apiPostAuth(`api/v0/DataIntegration/GetTableDataIntegrationDetail`, request, callback, error);
      },
      AddDataDataIntegrationDetail(request, callback, error) {
        this.apiPostAuth(`api/v0/DataIntegration/AddDataDataIntegrationDetail`, request, callback, error);
      },
      UpdateDataDataIntegrationDetail(request, callback, error) {
        this.apiPostAuth(`api/v0/DataIntegration/UpdateDataDataIntegrationDetail`, request, callback, error);
      },
      DeleteDataDataIntegrationDetail(request, callback, error) {
        this.apiPostAuth(`api/v0/DataIntegration/DeleteDataDataIntegrationDetail`, request, callback, error);
      },

      //Intent
      GetTableIntentGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/Intent/GetTableIntentGroup`, request, callback, error);
      },
      GetTableIntentSession(request, callback, error) {
        this.apiPostAuth(`api/v0/Intent/GetTableIntentSession`, request, callback, error);
      },
      GetIntentSessionDetailById(id, callback, error) {
        this.apiGetAuth(`api/v0/Intent/GetIntentSessionDetailById?Id=${id}`, callback, error);
      },
      DDIntentSession(callback, error) {
        this.apiGetAuth(`api/v0/Intent/DDIntentSession`, callback, error);
      },
      DDIntentGroup(callback, error) {
        this.apiGetAuth(`api/v0/Intent/DDIntentGroup`, callback, error);
      },
      CreateIntentGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/Intent/CreateIntentGroup`, request, callback, error);
      },
      UpdateIntentGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/Intent/UpdateIntentGroup`, request, callback, error);
      },
      DeleteIntentGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/Intent/DeleteIntentGroup`, request, callback, error);
      },
      CreateIntentSession(request, callback, error) {
        this.apiPostAuth(`api/v0/Intent/CreateIntentSession`, request, callback, error);
      },
      UpdateIntentSession(request, callback, error) {
        this.apiPostAuth(`api/v0/Intent/UpdateIntentSession`, request, callback, error);
      },
      DeleteIntentSession(request, callback, error) {
        this.apiPostAuth(`api/v0/Intent/DeleteIntentSession`, request, callback, error);
      },

      //ModelAndVectorManagement
      GetVectorDBNameMappinghGPTModel(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/GetVectorDBNameMappinghGPTModel', request, callback, error);
      },
      GetVectorDBNameMappinghGPTModelByName(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/GetVectorDBNameMappinghGPTModelByName', request, callback, error);
      },
      GetVectorDBNameMappinghGPTModelById(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/GetVectorDBNameMappinghGPTModelById', request, callback, error);
      },
      GetDDVectorDBName(callback, error) {
        this.apiGetAuth('api/v0/ManageGPTModelNode/GetDDVectorDBName', callback, error);
      },
      UpdateVectorDBNameMappinghGPTModel(request, callback, error) {
        this.apiPutAuth(`api/v0/ManageGPTModelNode/UpdateVectorDBNameMappinghGPTModel`, request, callback, error);
      },
      DeleteVectorDBNameMappinghGPTModel(param, callback, error) {
        this.apiDeleteAuth(`api/v0/ManageGPTModelNode/DeleteVectorDBNameMappinghGPTModel?vectorDBName=${param}`, callback, error);
      },
      // Assistant setup wizard — ผูกเอกสารเข้ากับ vectordbName ของโมเดล (เพิ่มเข้าไป ไม่ทับของเดิม)
      // ‼ ตัวนี้คือสิ่งที่ทำให้ผู้ช่วยเห็นเอกสารจริง — UpdateVectorDBNameMappinghGPTModel แก้แค่ tblvector.modelid ซึ่ง RAG ไม่ได้ใช้
      AttachVectorDBNameToGPTModel(request, callback, error) {
        this.apiPostAuth('api/v0/ManageGPTModelNode/AttachVectorDBNameToGPTModel', request, callback, error);
      },
      GetGPTModelWebSearchDomains(modelId, callback, error) {
        this.apiGetAuth(`api/v0/ManageGPTModelNode/GetGPTModelWebSearchDomains?modelId=${modelId}`, callback, error);
      },
      UpdateGPTModelWebSearchDomains(request, callback, error) {
        this.apiPutAuth(`api/v0/ManageGPTModelNode/UpdateGPTModelWebSearchDomains`, request, callback, error);
      },

      //Userprofile
      GetUserById(callback, error) {
        this.apiGetAuth(`api/v0/User/GetUserById`, callback, error);
      },
      GetVerifiedIdentity(callback, error) {
        this.apiGetAuth(`api/v0/User/GetVerifiedIdentity`, callback, error);
      },

      // 2FA — Login flow (unauthenticated, must send credentials so auth cookie is stored by browser)
      // Uses apiPost (same transport as LoginFullStream) — withCredentials:true so the Set-Cookie
      // from SignInAsync is stored by the browser; apiPostLogin is not used here because
      // it also does client-side Cookies.set/store mutations that are only valid for the regular
      // Login path (token in response body). VerifyLoginOtp success returns the same shape as
      // LoginFullStream (userId, username, etc.) which the caller handles directly.
      VerifyLoginOtp(request, callback, error) {
        this.apiPost('api/v0/Login/VerifyLoginOtp', request, callback, error);
      },
      ResendLoginOtp(request, callback, error) {
        this.apiPost('api/v0/Login/ResendLoginOtp', request, callback, error);
      },
      // 2FA หลัง login ผ่าน DGA Digital ID — { Code, Otp } โดย Code = code รอยืนยันที่ callback ส่งกลับมา
      // (portal: ?sso_mfa=, widget: sso_code ที่ snippet-exchange ตอบ requireTwoFactor). สำเร็จ: portal ได้ { redirect },
      // widget ได้ shape เดียวกับ SnippetSsoExchange
      SsoMfaVerify(request, callback, error) {
        this.apiPost('api/v0/LoginDGADigitalID/mfa-verify', request, callback, error);
      },
      SsoMfaResend(request, callback, error) {
        this.apiPost('api/v0/LoginDGADigitalID/mfa-resend', request, callback, error);
      },

      // 2FA — Profile enable/disable (authenticated)
      RequestEnableTwoFactor(callback, error) {
        this.apiPostAuth('api/v0/User/RequestEnableTwoFactor', null, callback, error);
      },
      ConfirmEnableTwoFactor(request, callback, error) {
        this.apiPostAuth('api/v0/User/ConfirmEnableTwoFactor', request, callback, error);
      },
      RequestDisableTwoFactor(callback, error) {
        this.apiPostAuth('api/v0/User/RequestDisableTwoFactor', null, callback, error);
      },
      ConfirmDisableTwoFactor(request, callback, error) {
        this.apiPostAuth('api/v0/User/ConfirmDisableTwoFactor', request, callback, error);
      },
      // ปิด 2FA โดยไม่ต้อง OTP (ยืนยันผ่าน dialog ที่หน้า Profile)
      DisableTwoFactor(callback, error) {
        this.apiPostAuth('api/v0/User/DisableTwoFactor', null, callback, error);
      },

      //LogMessage
      GetLogReferenceFromLog(request, callback, error) {
        this.apiPostAuth('api/v0/Log/GetLogReference', request, callback, error);
      },
      GetLogMonitorFromLog(request, callback, error) {
        this.apiPostAuth('api/v0/Log/GetLogMonitor', request, callback, error);
      },
      GetLogDocumentReferenceFromLog(request, callback, error) {
        this.apiPostAuth('api/v0/Log/GetLogDocumentReference', request, callback, error);
      },
      GetTableLogMessage(request, callback, error) {
        this.apiPostAuth('api/v0/Log/GetTableLogMessage', request, callback, error);
      },
      GetAiModelState(request, callback, error) {
        this.apiPostAuth('api/v0/Log/GetAiModelState', request, callback, error);
      },
      GetExportCSVLogMessage(request, callback, error) {
        this.apiPostAuth('api/v0/Log/ExportCSVLogMessage', request, callback, error);
      },
      GetExportLogMonitoring(request, callback, error) {
        this.apiPostTypeFileAuth(`api/v0/Log/ExportLogMonitoring`, request, callback, error);
      },
      GetExportLog(request, callback, error) {
        this.apiPostTypeFileAuth(`api/v0/Log/ExportLog`, request, callback, error);
      },
      ExportFileQuestionSet(request, callback, error) {
        this.apiPostTypeFileAuth(`api/v0/Log/ExportFileQuestionSet`, request, callback, error);
      },
      ExportQuestionSet(request, callback, error) {
        this.apiPostAuth(`api/v0/Log/ExportQuestionSet`, request, callback, error);
      },
      GetEventLog(request, callback, error) {
        this.apiPostAuth('api/v0/Log/EventLogShow', request, callback, error);
      },
      GetApiExternal(request, callback, error) {
        this.apiPostAuth('api/v0/Log/GetApiExternal', request, callback, error);
      },
      GetApiExternalById(id, callback, error) {
        this.apiGetAuth(`api/v0/Log/GetApiExternalById?id=${id}`, callback, error);
      },
      ExportApiExternal(request, callback, error) {
        this.apiPostTypeFileAuth('api/v0/Log/ExportApiExternal', request, callback, error);
      },

      //Dashboard
      GetDashboardOverview(request, callback, error) {
        this.apiPostAuth('api/v0/Dashboard/Overview', request, callback, error);
      },
      GetTokenDetail(request, callback, error) {
        // ใช้ apiPostAuthNoAlert เพื่อไม่ถูก logout เมื่อ endpoint นี้ fail
        // (GetTokenDetail เป็น call เสริม ไม่ใช่ critical auth endpoint)
        this.apiPostAuthNoAlert('api/v0/Dashboard/TokenDetail', request, callback, error);
      },
      GetPageTokenDetail(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/Dashboard/PageTokenDetail', request, callback, error);
      },
      GetDashboardSystemUsage(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/Dashboard/SystemUsage', request, callback, error);
      },
      GetDashboardQuestions(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/Dashboard/Questions', request, callback, error);
      },
      GetDashboardLikeDislike(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/Dashboard/LikeDislike', request, callback, error);
      },
      ExportDashboardOverview(request, callback, error) {
        this.apiPostTypeFileAuth('api/v0/Dashboard/ExportOverview', request, callback, error);
      },
      ExportDashboardTokenDetail(request, callback, error) {
        this.apiPostTypeFileAuth('api/v0/Dashboard/ExportTokenDetail', request, callback, error);
      },
      ExportDashboardSystemUsage(request, callback, error) {
        this.apiPostTypeFileAuth('api/v0/Dashboard/ExportSystemUsage', request, callback, error);
      },
      ExportDashboardLikeDislike(request, callback, error) {
        this.apiPostTypeFileAuth('api/v0/Dashboard/ExportLikeDislike', request, callback, error);
      },
      ExportDashboardAll(request, callback, error) {
        this.apiPostTypeFileAuth('api/v0/Dashboard/ExportAll', request, callback, error);
      },

      // SuperAdmin Dashboard
      SuperAdminDashboardOverview(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/SuperAdminDashboard/Overview', request, callback, error);
      },
      SuperAdminDashboardTokenDetail(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/SuperAdminDashboard/TokenDetail', request, callback, error);
      },
      SuperAdminDashboardPageTokenDetail(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/SuperAdminDashboard/PageTokenDetail', request, callback, error);
      },
      SuperAdminDashboardSystemUsage(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/SuperAdminDashboard/SystemUsage', request, callback, error);
      },
      SuperAdminDashboardQuestions(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/SuperAdminDashboard/Questions', request, callback, error);
      },
      SuperAdminDashboardLikeDislike(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/SuperAdminDashboard/LikeDislike', request, callback, error);
      },
      SuperAdminDashboardCompanyDetails(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/SuperAdminDashboard/CompanyDetails', request, callback, error);
      },
      SuperAdminDashboardExportCompanyDetails(request, callback, error) {
        this.apiPostTypeFileAuth('api/v0/SuperAdminDashboard/ExportCompanyDetails', request, callback, error);
      },
      SuperAdminDashboardExportAll(request, callback, error) {
        this.apiPostTypeFileAuth('api/v0/SuperAdminDashboard/ExportAll', request, callback, error);
      },
      SuperAdminGetDocumentCounts(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/SuperAdminDashboard/GetDocumentCounts', request, callback, error);
      },
      SuperAdminGetDocumentsByCompany(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/SuperAdminDashboard/GetDocumentsByCompany', request, callback, error);
      },

      // SuperAdmin Notification
      SuperAdminNotificationGetList(params, callback, error) {
        const query = new URLSearchParams(params).toString();
        this.apiGetAuth(`api/v0/SuperAdminNotification/GetList?${query}`, callback, error);
      },
      SuperAdminNotificationGetListPaged(params, callback, error) {
        const query = new URLSearchParams(params).toString();
        this.apiGetAuth(`api/v0/SuperAdminNotification/GetListPaged?${query}`, callback, error);
      },
      SuperAdminNotificationGetUnreadCount(callback, error) {
        this.apiGetAuth('api/v0/SuperAdminNotification/GetUnreadCount', callback, error);
      },
      SuperAdminNotificationMarkAsRead(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminNotification/MarkAsRead', request, callback, error);
      },
      SuperAdminNotificationMarkAllAsRead(callback, error) {
        this.apiPostAuth('api/v0/SuperAdminNotification/MarkAllAsRead', null, callback, error);
      },
      SuperAdminNotificationSendTest(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminNotification/SendTestNotification', request, callback, error);
      },

      //ChatServices
      // GetTopicAll(callback, error){
      //   this.apiGetAuth('api/v0/ChatHub/GetTopicAll', callback, error);
      // },
      GetTopicAll(request, callback, error) {
        this.apiPostAuth('api/v0/ChatHub/GetTopicAll', request, callback, error);
      },
      GetHistoryMessage(request, Config, callback, error) {
        this.apiPostAuthNoAlert_Config(`api/v0/ChatHub/GetHistoryMessage`, request, Config, callback, error);
      },
      AdminReplyStatus(id, status, callback, error) {
        this.apiGetAuth(`api/v0/ChatHub/AdminReplyStatus?id=${id}&status=${status}`, callback, error);
      },
      LatestMessage(request, callback, error) {
        this.apiPostAuth('api/v0/ChatHub/LatestMessage', request, callback, error);
      },
      MarkAsRead(request, callback, error) {
        this.apiPostAuth('api/v0/Chathub/MarkAsRead', request, callback, error);
      },
      TopicUnread(callback, error) {
        this.apiGetAuth('api/v0/ChatHub/TopicUnread', callback, error);
      },
      //Setup
      // GetSetupOverview(request, callback, error) {
      //     const name = request.name;
      //     const index = request.index;
      //     const size = request.size;
      //     this.apiGetAuth(`api/v0/Setup/Overview?name=${name}&index=${index}&size=${size}`, callback, error);
      // },
      GetSetupOverview(callback, error) {
        this.apiGetAuth(`api/v0/Setup/Overview`, callback, error);
      },
      GetSetupOverviewGeneral(callback, error) {
        this.apiGetAuth(`api/v0/Setup/OverviewGeneral`, callback, error);
      },
      GetOverviewSocial(callback, error) {
        this.apiGetAuth(`api/v0/Setup/OverviewSocial`, callback, error);
      },
      GetTableGroupModel(request, callback, error) {
        this.apiPostAuth(`api/v0/Setup/GetTableGroupModel`, request, callback, error);
      },
      GetDDGroupModel(callback, error) {
        this.apiGetAuth(`api/v0/Setup/GetDDGroupModel`, callback, error);
      },
      GetSetupCreate(request, callback, error) {
        this.apiPostAuth('api/v0/Setup/Create', request, callback, error);
      },
      CreateGroupModel(request, callback, error) {
        this.apiPostAuth('api/v0/Setup/CreateGroupModel', request, callback, error);
      },
      GetSetupUpdate(request, callback, error) {
        this.apiPostAuth('api/v0/Setup/Update', request, callback, error);
      },
      GetSetupUpdateGeneral(request, callback, error) {
        this.apiPostAuth('api/v0/Setup/UpdateGeneral', request, callback, error);
      },
      UpdateSocial(request, callback, error) {
        this.apiPostAuth('api/v0/Setup/UpdateSocial', request, callback, error);
      },
      GetEntraAppConfigList(callback, error) {
        this.apiGetAuth('api/v0/EntraAppConfig/GetList', callback, error);
      },
      GetEntraAppConfigSecret(id, callback, error) {
        this.apiGetAuth(`api/v0/EntraAppConfig/GetSecret?id=${encodeURIComponent(id)}`, callback, error);
      },
      SaveEntraAppConfig(request, callback, error) {
        this.apiPostAuth('api/v0/EntraAppConfig/Save', request, callback, error);
      },
      DeleteEntraAppConfig(request, callback, error) {
        this.apiPostAuth('api/v0/EntraAppConfig/Delete', request, callback, error);
      },
      GetLineEntraLinkList(params, callback, error) {
        const p = params || {};
        const q = [];
        if (p.entraAppConfigId) q.push(`entraAppConfigId=${encodeURIComponent(p.entraAppConfigId)}`);
        if (p.search) q.push(`search=${encodeURIComponent(p.search)}`);
        if (p.includeInactive) q.push(`includeInactive=true`);
        const qs = q.length ? `?${q.join('&')}` : '';
        this.apiGetAuth(`api/v0/LineEntraLink/GetList${qs}`, callback, error);
      },
      UnlinkLineEntraLink(request, callback, error) {
        this.apiPostAuth('api/v0/LineEntraLink/Unlink', request, callback, error);
      },
      UpdateGroupModel(request, callback, error) {
        this.apiPostAuth('api/v0/Setup/UpdateGroupModel', request, callback, error);
      },
      GetSetupDelete(id, callback, error) {
        this.apiDeleteAuth(`api/v0/Setup/Delete?id=${id}`, callback, error);
      },
      DeleteGroupModel(id, callback, error) {
        this.apiDeleteAuth(`api/v0/Setup/DeleteGroupModel?id=${id}`, callback, error);
      },
      GetSetupApply(callback, error) {
        this.apiPostAuth(`api/v0/Setup/Apply`, callback, error);
      },

      //ModelQuick
      GetTableModelQuickGroup(callback, error) {
        this.apiPostAuth(`api/v0/ModelQuick/GetTableModelQuickGroup`, callback, error);
      },
      CreateModelQuickGroup(request, callback, error) {
        this.apiPostAuth('api/v0/ModelQuick/CreateModelQuickGroup', request, callback, error);
      },
      UpdateModelQuickGroup(request, callback, error) {
        this.apiPostAuth('api/v0/ModelQuick/UpdateModelQuickGroup', request, callback, error);
      },
      DeleteModelQuickGroup(id, callback, error) {
        this.apiDeleteAuth(`api/v0/ModelQuick/DeleteModelQuickGroup?id=${id}`, callback, error);
      },
      GetDDModelQuickGroup(callback, error) {
        this.apiGetAuth(`api/v0/ModelQuick/GetDDModelQuickGroup`, callback, error);
      },
      GetDDModelQuick(request, callback, error) {
        this.apiPostAuth('api/v0/ModelQuick/GetDDModelQuick', request, callback, error);
      },

      //Quick reply
      GetDDQuickReplyGroup(callback, error) {
        this.apiGetAuth(`api/v0/ModelQuick/GetDDQuickReplyGroup`, callback, error);
      },
      GetDDModelQuickReply(request, callback, error) {
        this.apiPostAuth('api/v0/ModelQuick/GetDDModelQuickReply', request, callback, error);
      },
      GetQuickReplyGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/ModelQuick/GetQuickReplyGroup`, request, callback, error);
      },
      CreateQuickReplyGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/ModelQuick/CreateQuickReplyGroup`, request, callback, error);
      },
      DeleteQuickReplyGroup(id, callback, error) {
        this.apiDeleteAuth(`api/v0/ModelQuick/DeleteQuickReplyGroup?id=${id}`, callback, error);
      },
      UpdateModelQuickReply(request, callback, error) {
        this.apiPostAuth('api/v0/ModelQuick/UpdateModelQuickReply', request, callback, error);
      },
      GetModelQuickGroup(callback, error) {
        this.apiGetAuth(`api/v0/ModelQuick/GetModelQuickGroup`, callback, error);
      },
      GetModelGroupAndQuickReply(id, callback, error) {
        this.apiGetAuth(`api/v0/ModelQuick/GetModelGroupAndQuickReply?id=${id}`, callback, error);
      },
      GetModelQuickCommand(id, callback, error) {
        this.apiGetAuth(`api/v0/ModelQuick/GetModelQuickCommand?id=${id}`, callback, error);
      },
      UploadImageModelQuick(request, callback, error) {
        let url = `api/v0/ModelQuick/UploadFileImageModelQuick`;
        const formData = new FormData();
        formData.append('entity', request.entity);
        this.apiPostImportFileAuth(url, formData, { withCredentials: true }, callback, error);
      },

      //Replace text
      GetTableGroupReplaceText(callback, error) {
        this.apiPostAuth(`api/v0/ReplaceText/GetTableGroupReplaceText`, callback, error);
      },
      GetTableReplaceText(callback, error) {
        this.apiPostAuth(`api/v0/ReplaceText/GetTableReplaceText`, callback, error);
      },
      CreateGroupReplace(callback, error) {
        this.apiPostAuth(`api/v0/ReplaceText/CreateGroupReplace`, callback, error);
      },
      CreateReplaceText(callback, error) {
        this.apiPostAuth(`api/v0/ReplaceText/CreateReplaceText`, callback, error);
      },
      UpdateReplaceText(callback, error) {
        this.apiPostAuth(`api/v0/ReplaceText/UpdateReplaceText`, callback, error);
      },
      DeleteGroupReplaceText(id, callback, error) {
        this.apiDeleteAuth(`api/v0/ReplaceText/DeleteGroupReplaceText?id=${id}`, callback, error);
      },

      DeleteReplaceText(id, callback, error) {
        this.apiDeleteAuth(`api/v0/ReplaceText/DeleteReplaceText?id=${id}`, callback, error);
      },
      GetDDGroupReplaceDB(callback, error) {
        this.apiGetAuth('api/v0/ReplaceText/GetDDGroupReplaceDB', callback, error);
      },
      UploadFileReplaceText(request, callback, error) {
        this.apiPostTypeFileNoResponseAuth(
          `api/v0/ReplaceText/UploadFileReplaceText?groupName=${request.groupName}&deleteAll=${request.DeleteAll}&groupReplaceID=${request.groupReplaceID}`,
          request.uploaded_file,
          callback,
          error
        );
      },

      //Flow Management
      GetTableFlowManagement(request, callback, error) {
        this.apiPostAuth('api/v0/Flow/GetTableFlowManagement', request, callback, error);
      },
      GetFlowById(id, callback, error) {
        this.apiGetAuth(`api/v0/Flow/GetFlowById?id=${id}`, callback, error);
      },
      GetFlowByGroupFlowId(groupFlowId, callback, error) {
        this.apiGetAuth(`api/v0/Flow/GetFlowByGroupFlowId?groupFlowId=${groupFlowId}`, callback, error);
      },
      GetFlowVersions(groupFlowId, callback, error) {
        this.apiGetAuth(`api/v0/Flow/GetFlowVersions?groupFlowId=${groupFlowId}`, callback, error);
      },
      CreateFlow(request, callback, error) {
        this.apiPostAuth('api/v0/Flow/CreateFlow', request, callback, error);
      },
      UpdateFlow(request, callback, error) {
        this.apiPostAuth('api/v0/Flow/UpdateFlow', request, callback, error);
      },
      RestoreFlowVersion(request, callback, error) {
        this.apiPostAuth('api/v0/Flow/RestoreFlowVersion', request, callback, error);
      },
      // Flow Debug Mode (docs/plans/2026-10-30-flow-debug-mode-plan.md) — DebugRun ตอบเป็น SSE (text/event-stream):
      // ผู้เรียกอ่าน event ทีละก้อนจาก Config.onDownloadProgress (pattern เดียวกับ GptStreamPOST) และยกเลิกด้วย Config.signal
      FlowDebugRun(request, Config, callback, error) {
        this.apiPostText_Stream('api/v0/Flow/DebugRun', { ...Config, withCredentials: true, responseType: 'text' }, request, callback, error);
      },
      FlowDebugResume(request, callback, error) {
        this.apiPostAuth('api/v0/Flow/DebugResume', request, callback, error);
      },
      FlowDebugStop(request, callback, error) {
        this.apiPostAuth('api/v0/Flow/DebugStop', request, callback, error);
      },
      DeleteFlow(id, callback, error) {
        this.apiPostAuth('api/v0/Flow/DeleteFlow', id, callback, error);
      },
      GetFlowDropdown(callback, error) {
        this.apiGetAuth('api/v0/Flow/GetFlowDropdown', callback, error);
      },
      TestAPIKnowledge(request, callback, error) {
        this.apiPostAuth('api/v0/Flow/TestAPIKnowledge', request, callback, error);
      },
      DiscoverMcpTools(request, callback, error) {
        this.apiPostAuth('api/v0/FlowEngine/DiscoverMcpTools', request, callback, error);
      },

      //Validation Generate
      GetTableValidationGenerate(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationGenerate/GetTableValidationGenerate`, request, callback, error);
      },
      CreateValidationGenerate(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationGenerate/CreateValidationGenerate`, request, callback, error);
      },
      UploadValidationGenerate(request, callback, error) {
        this.apiPostTypeFileNoResponseAuth(`api/v0/ValidationGenerate/UploadValidationGenerate?id=${request.id}`, request.entity, callback, error);
      },
      DownloadCSVQuestions(param, callback, error) {
        this.apiGetAuth(`api/v0/ValidationGenerate/DownloadCSVQuestions?id=${param}`, callback, error);
      },
      ReprocessValidationGenerate(param, callback, error) {
        this.apiGetAuth(`api/v0/ValidationGenerate/ReprocessValidationGenerate?id=${param}`, callback, error);
      },
      GetValidationGenerateDD(callback, error) {
        this.apiGetAuth(`api/v0/ValidationGenerate/GetValidationGenerateDD`, callback, error);
      },
      GetValidationGenerateDetail(param, callback, error) {
        this.apiGetAuth(`api/v0/ValidationGenerate/GetValidationGenerateDetail?GenerateID=${param}`, callback, error);
      },
      EditValidationGenerate(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationGenerate/EditValidationGenerate`, request, callback, error);
      },
      DeleteValidationGenerate(param, callback, error) {
        this.apiGetAuth(`api/v0/ValidationGenerate/DeleteValidationGenerate?GenerateID=${param}`, callback, error);
      },

      //Validation Process
      GetTableValidationProcess(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationProcess/GetTableValidationProcess`, request, callback, error);
      },
      CreateValidationProcess(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationProcess/CreateValidationProcess`, request, callback, error);
      },
      EditValidationProcess(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationProcess/EditValidationProcess`, request, callback, error);
      },
      UploadValidationProcess(request, callback, error) {
        this.apiPostTypeFileNoResponseAuth(`api/v0/ValidationProcess/UploadValidationProcess?id=${request.id}&GroupQuestion=${request.GroupQuestion}`, request.entity, callback, error);
      },
      DownloadCSVAnswer(request, callback, error) {
        this.apiGetAuth(`api/v0/ValidationProcess/DownloadCSVAnswer?id=${request.id}&IsGroup=${request.isGroup}`, callback, error);
      },
      ReprocessValidationProcess(param, callback, error) {
        this.apiGetAuth(`api/v0/ValidationProcess/ReprocessValidationProcess?id=${param}`, callback, error);
      },
      SetReprocessValidationProcess(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationProcess/SetReprocessValidationProcess`, request, callback, error);
      },
      GenerateValidationScore(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationProcess/GenerateValidationScore`, request, callback, error);
      },
      ReGenerateValidationScore(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationProcess/ReGenerateValidationScore`, request, callback, error);
      },
      GetTableValidationScore(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationProcess/GetTableValidationScore`, request, callback, error);
      },
      GetDashboardValidationScore(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationProcess/GetDashboardValidationScore`, request, callback, error);
      },
      GetDDValidationProcess(callback, error) {
        this.apiGetAuth(`api/v0/ValidationProcess/DDValidationProcess`, callback, error);
      },
      GetValidationProcessDetail(param, callback, error) {
        this.apiGetAuth(`api/v0/ValidationProcess/GetValidationProcessDetail?ProcessID=${param}`, callback, error);
      },
      DeleteValidationProcess(param, callback, error) {
        this.apiDeleteAuth(`api/v0/ValidationProcess/DeleteValidationProcess?ProcessID=${param}`, callback, error);
      },
      ValidateCSV(formFile, callback, error) {
        this.apiPostTypeFileNoResponseAuth(`api/v0/ValidationProcess/ValidateCSV`, formFile, callback, error);
      },

      //Validation Positive
      GetTableValidationPositive(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationPositive/GetTableValidationPositive`, request, callback, error);
      },
      GetTableValidationPositiveDetail(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationPositive/GetTableValidationPositiveDetail`, request, callback, error);
      },
      UploadValidationPositive(request, callback, error) {
        this.apiPostTypeFileNoResponseAuth(`api/v0/ValidationPositive/UploadValidationPositive?name=${request.name}&type=${request.type}`, request.entity, callback, error);
      },
      DeleteValidationPositive(request, callback, error) {
        this.apiDeleteAuth(`api/v0/ValidationPositive/ValidationPositive?name=${request}`, callback, error);
      },
      AddValidationPositiveDetail(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationPositive/ValidationPositiveDetailAdd`, request, callback, error);
      },
      EditValidationPositiveDetail(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationPositive/ValidationPositiveDetailEdit`, request, callback, error);
      },
      DeleteValidationPositiveDetail(request, callback, error) {
        this.apiDeleteAuth(`api/v0/ValidationPositive/ValidationPositiveDetailDelete?id=${request}`, callback, error);
      },

      //Validation Negative
      GetTableValidationNegative(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationNegative/GetTableValidationNegative`, request, callback, error);
      },
      GetTableValidationNegativeDetail(request, callback, error) {
        this.apiPostAuth(`api/v0/ValidationNegative/GetTableValidationNegativeDetail`, request, callback, error);
      },
      UploadValidationNegative(request, callback, error) {
        this.apiPostTypeFileNoResponseAuth(`api/v0/ValidationNegative/UploadValidationNegative?name=${request.name}`, request.entity, callback, error);
      },
      DeleteValidationNegative(request, callback, error) {
        this.apiDeleteAuth(`api/v0/ValidationNegative/ValidationNegativeDelete?name=${request}`, callback, error);
      },
      DeleteValidationNegativeDetail(request, callback, error) {
        this.apiDeleteAuth(`api/v0/ValidationNegative/ValidationNegativeDetailDelete?id=${request}`, callback, error);
      },

      //#region Folder
      GetFolders(callback, error) {
        this.apiPostAuth('api/v0/Folder/GetFolders', null, callback, error);
      },
      GetFolderById(id, callback, error) {
        this.apiGetAuth(`api/v0/Folder/GetFolderById?id=${id}`, callback, error);
      },
      CreateFolder(request, callback, error) {
        this.apiPostAuth('api/v0/Folder/CreateFolder', request, callback, error);
      },
      UpdateFolder(request, callback, error) {
        this.apiPostAuth('api/v0/Folder/UpdateFolder', request, callback, error);
      },
      DeleteFolder(request, callback, error) {
        this.apiPostAuth('api/v0/Folder/DeleteFolder', request, callback, error);
      },
      AddTopicToFolder(request, callback, error) {
        this.apiPostAuth('api/v0/Folder/AddTopicToFolder', request, callback, error);
      },
      RemoveTopicFromFolder(request, callback, error) {
        this.apiPostAuth('api/v0/Folder/RemoveTopicFromFolder', request, callback, error);
      },
      MoveTopicToFolder(request, callback, error) {
        this.apiPostAuth('api/v0/Folder/MoveTopicToFolder', request, callback, error);
      },
      // หน้าแชท (FullStreamV2) — ไม่ล้าง session / พาไป /login เมื่อ 401: ผู้ใช้ที่กู้ session จาก token อย่างเดียว
      // (cookie หมด/ถูกบล็อกใน iframe) ยังแชทต่อได้ แค่ไม่มี folder
      GetFoldersChat(callback, error) {
        this.apiPostAuthNoAlert('api/v0/Folder/GetFolders', null, callback, error);
      },
      CreateFolderChat(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/Folder/CreateFolder', request, callback, error);
      },
      UpdateFolderChat(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/Folder/UpdateFolder', request, callback, error);
      },
      DeleteFolderChat(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/Folder/DeleteFolder', request, callback, error);
      },
      AddTopicToFolderChat(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/Folder/AddTopicToFolder', request, callback, error);
      },
      RemoveTopicFromFolderChat(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/Folder/RemoveTopicFromFolder', request, callback, error);
      },
      MoveTopicToFolderChat(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/Folder/MoveTopicToFolder', request, callback, error);
      },
      //#endregion

      // Master Data
      GetModelData(request, callback, error) {
        this.apiPostAuth(`api/v0/ModelData/GetModelData`, request, callback, error);
      },
      // GetAllLLMModelData(Token, callback, error) {
      //   Token = Token == null ? '' : Token;
      //   this.apiGetToken('api/v0/GPTStream/GetALLModelData', callback, error, Token);
      // },
      GetDDLModelData(callback, error) {
        this.apiGetAuth(`api/v0/ModelData/GetDDLModelData`, callback, error);
      },
      // S25-P1 — dropdown "โมเดลผู้ให้คะแนน" (lean: ไม่มี endpoint/key)
      GetDDLScoreModelData(callback, error) {
        this.apiGetAuth(`api/v0/ModelData/GetDDLScoreModelData`, callback, error);
      },
      CreateModelData(request, callback, error) {
        this.apiPostAuth(`api/v0/ModelData/CreateModelData`, request, callback, error);
      },
      UpdateModelData(request, callback, error) {
        this.apiPostAuth(`api/v0/ModelData/UpdateModelData`, request, callback, error);
      },
      DeleteModelData(request, callback, error) {
        this.apiPostAuth(`api/v0/ModelData/DeleteModelData`, request, callback, error);
      },

      GetPromptTemplate(request, callback, error) {
        this.apiPostAuth(`api/v0/ModelData/GetPromptTemplate`, request, callback, error);
      },
      AddPromptTemplate(request, callback, error) {
        this.apiPostAuth(`api/v0/ModelData/AddPromptTemplate`, request, callback, error);
      },
      UpdatePromptTemplate(request, callback, error) {
        this.apiPostAuth(`api/v0/ModelData/UpdatePromptTemplate`, request, callback, error);
      },
      DeletePromptTemplate(request, callback, error) {
        this.apiPostAuth(`api/v0/ModelData/DeletePromptTemplate`, request, callback, error);
      },
      GetPromptTemplateReadyUse(callback, error) {
        let request = {};
        this.apiPostAuth(`api/v0/ModelData/GetPromptTemplateReadyUse`, request, callback, error);
      },
      GetProviderData(request, callback, error) {
        this.apiPostAuth(`api/v0/ProviderData/GetProviderData`, request, callback, error);
      },
      GetDDLProviderData(callback, error) {
        this.apiGetAuth(`api/v0/ProviderData/GetDDLProviderData`, callback, error);
      },
      GetKeyJSON(callback, error) {
        let request = {};
        this.apiPostAuth(`api/v0/ModelData/GetKeyJSON`, request, callback, error);
      },

      //# region Stream
      ManageSnippetToken(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/GPTStream/ManageSnippetToken', request, callback, error);
      },
      QuickReplyMobile(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStreamQuickReply/QuickReplyMobile', request, callback, error);
      },
      QuickReplyWeb(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStreamQuickReply/QuickReplyWeb', request, callback, error);
      },
      QuickReplyFullStream(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStreamQuickReply/QuickReply', request, callback, error);
      },
      QuickReplyFullStreamToken(request, Token, callback, error) {
        this.apiPostAuthNoAlertToken('api/v0/GPTStreamQuickReply/QuickReply', request, callback, error, Token);
      },
      GetTokenFullStream(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStream/GetTokenFullStream', request, callback, error);
      },
      GetTokenFullStreamSafe(request, callback, error) {
        // Variant ที่ไม่ clear cookies / redirect ไป /login เมื่อ 401
        // ใช้ตอน init / F5 ของ FullStream V2 — ให้ caller fallback ไป anonymous เอง
        const instance = axios.create({
          withCredentials: true,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .post(this.urlApi + 'api/v0/GPTStream/GetTokenFullStream', request)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((err) => {
            return error(err);
          });
      },
      ChkTokenFullStream(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStream/ChkTokenFullStream', request, callback, error);
      },
      // ต่ออายุ Bearer ของหน้าแชท — token เดิมส่งใน body เหมือน ChkTokenFullStream (server ตรวจ signature, รับที่หมดอายุไม่เกิน 24 ชม.)
      RenewFullStreamToken(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStream/RenewFullStreamToken', request, callback, error);
      },
      GptFullStreamPOST(request, Config, callback, error) {
        this.apiPostText_Stream('api/v0/GPTStream/GptFullStreamPOST', Config, request, callback, error);
      },
      GptStreamPOST(request, Config, callback, error) {
        this.apiPostText_Stream('api/v0/GPTStream/GptStreamPOST', Config, request, callback, error);
      },
      GetValidationNegativeDD(callback, error) {
        this.apiGetAuth(`api/v0/ValidationNegative/GetValidationNegativeDD`, callback, error);
      },
      GetValidationPositiveDD(callback, error) {
        this.apiGetAuth(`api/v0/ValidationPositive/GetValidationPositiveDD`, callback, error);
      },
      GetValidationPositiveDD2(callback, error) {
        this.apiGetAuth(`api/v0/ValidationPositive/GetValidationPositiveDD2`, callback, error);
      },

      GetLogStream(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStream/GetLogStream', request, callback, error);
      },
      GetLogMonitor(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStream/GetLogMonitor', request, callback, error);
      },

      GptFullStreamPostToken(request, Config, Token, callback, error, query) {
        // query (optional) = snippet context (snippet/domainName/companyId/modelId) แนบกับ URL ตรง ๆ —
        // backend (AttachFullStreamQueryContext) อ่าน Request.Query ก่อน Referer ทำให้ resolve บริษัทได้
        // แม้ browser ตัด query ออกจาก Referer (เช่น Safari private mode) ซึ่งเดิมทำให้ตก default company
        const url = 'api/v0/GPTStream/GptFullStreamPOST' + (query ? `?${query}` : '');
        this.apiPostTokenText_Stream(url, Config, request, callback, error, Token);
      },
      ChatPlayGroundStreamPOST(request, Config, callback, error) {
        this.apiPostText_Stream('api/v0/GPTStream/ChatPlayGroundStreamPOST', Config, request, callback, error);
      },

      GetModelQuickGroupInModelGroup(request, Token, callback, error, query) {
        // query (optional) = snippet context เดียวกับ GptFullStreamPostToken — กัน Referer โดนตัดแล้ว
        // model config (IsAllowImage ฯลฯ) resolve ข้ามบริษัท
        const url = 'api/v0/GPTStream/GetModelQuickGroupInModelGroup' + (query ? `?${query}` : '');
        this.apiPostToken(url, request, callback, error, Token);
      },
      GetShortcutMenu(request, Token, callback, error) {
        this.apiPostToken('api/v0/GPTStream/GetShortcutMenu', request, callback, error, Token);
      },
      //#endregion

      //#region Speech_To_Text
      GetModelQuickGroupInModelGroupPostToken(request, Token, callback, error) {
        this.apiPostToken('api/v0/GPTStream/GetModelQuickGroupInModelGroup', request, callback, error, Token);
      },

      // Speech_To_Text
      GetConvest_Speech_To_Text(request, Config, callback, error) {
        this.apiPostImportFileAuth('api/v0/SpeechToText/Convest_Speech_To_Text', request, Config, callback, error);
      },
      GetSpeechDocument(request, callback, error) {
        this.apiPostAuth('api/v0/SpeechToText/SpeechDocumentShow', request, callback, error);
      },
      CreateSpeechDocument(request, config, callback, error) {
        this.apiPostImportFileAuth(`api/v0/SpeechToText/CreateSpeechDocument`, request, config, callback, error);
      },
      UploadSpeechDocument(request, config, callback, error) {
        this.apiPostImportFileAuth(`api/v0/SpeechToText/UploadSpeechDocument`, request, config, callback, error);
      },
      GetSpeechDocumentDetail(request, callback, error) {
        this.apiPostAuth('api/v0/SpeechToText/GetSpeechDocumentDetail', request, callback, error);
      },
      EditSpeechDocumentDetail(request, callback, error) {
        this.apiPostAuth(`api/v0/SpeechToText/EditSpeechDocument`, request, callback, error);
      },
      DeleteSpeechDocument(param, callback, error) {
        this.apiDeleteAuth(`api/v0/SpeechToText/DeleteSpeechDocument?id=${param}`, callback, error);
      },
      DeleteSpeechDocumentDetail(param, callback, error) {
        this.apiDeleteAuth(`api/v0/SpeechToText/DeleteSpeechDocumentDetail?id=${param}`, callback, error);
      },
      ImportSpeech2Text(request, callback, error) {
        this.apiPostAuth(`api/v0/Chat/ImportSpeech2Text`, request, callback, error);
      },
      SummarizedSpeechDocumentDetail(request, callback, error) {
        this.apiPostAuth(`api/v0/SpeechToText/SummarizedSpeechDocumentDetail`, request, callback, error);
      },
      //#endregion

      //#region History
      GetHistoryStream(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/GPTStream/GetHistoryStream', request, callback, error);
      },
      GetHistoryFullStream(request, token, callback, error) {
        this.apiPostAuthNoAlertToken('api/v0/GPTStream/GetHistoryFullStream', request, callback, error, token);
      },
      // GetLastLogID(request, callback, error) {
      //   this.apiPostAuthNoAlert('api/v0/GPTStream/GetLastLogID', request, callback, error);
      // },
      GetLastLogID(request, token, callback, error) {
        this.apiPostToken('api/v0/GPTStream/GetLastLogID', request, callback, error, token);
      },

      GetTopicStream(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/GPTStream/GetTopicStream', request, callback, error);
      },
      GetTopicFullStream(request, Token, callback, error) {
        this.apiPostToken('api/v0/GPTStream/GetTopicFullStream', request, callback, error, Token);
      },

      GetTopicStreamToken(request, Token, callback, error) {
        Token = Token == null ? '' : Token;
        this.apiPostAuthNoAlertToken('api/v0/GPTStream/GetTopicFullStream', request, callback, error, Token);
      },

      // RenameTopicStream(request, callback, error) {
      //   this.apiPostAuthNoAlert('api/v0/GPTStream/RenameTopicStream', request, callback, error);
      // },
      RenameTopicStream(request, Token, callback, error) {
        Token = Token == null ? '' : Token;
        this.apiPostToken('api/v0/GPTStream/RenameTopicStream', request, callback, error, Token);
      },
      DeleteTopicStream(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/GPTStream/DeleteTopicStream', request, callback, error);
      },
      MigrateAnonymousSession(request, Token, callback, error) {
        this.apiPostToken('api/v0/GPTStream/MigrateAnonymousSession', request, callback, error, Token);
      },
      CreatePortalHandoff(request, Token, callback, error) {
        this.apiPostToken('api/v0/Login/CreatePortalHandoff', request, callback, error, Token);
      },

      GetTopicIDStream(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStream/GetTopicIDStream', request, callback, error);
      },
      //#endregion

      //#region Private Memory

      GetDDGroupReplaceDB_Stream(callback, error) {
        this.apiGetAuth('api/v0/GPTStream/GetDDGroupReplaceDBStream', callback, error);
      },
      GetDDGroupReplaceDB_FullStream(Token, callback, error) {
        this.apiGetToken('api/v0/GPTStream/GetDDGroupReplaceDBFullStream', callback, error, Token);
      },
      UploadImage_Stream(request, callback, error) {
        let url = `api/v0/GPTStream/UploadImageFileStream?USER_CHANNEL=${request.USER_CHANNEL}`;
        this.apiPostTypeFileNoResponseAuth(url, request.entity, callback, error);
      },
      UploadImage_FullStream(request, Token, callback, error) {
        let url = `api/v0/GPTStream/UploadImageFileFullStream?USER_CHANNEL=${request.USER_CHANNEL}`;
        this.apiPostTypeFileNoResponseToken(url, request.entity, callback, error, Token);
      },
      ExtractFileText_FullStream(request, Token, callback, error) {
        const maxLimitedCharacter = encodeURIComponent(request.MaxLimitedCharacter || 0);
        this.apiPostTypeFileNoResponseToken(`api/v0/Chat/ExtractFileTextFullStream?MaxLimitedCharacter=${maxLimitedCharacter}`, request.entity, callback, error, Token);
      },
      ExtractFileText_Stream(request, callback, error) {
        const maxLimitedCharacter = encodeURIComponent(request.MaxLimitedCharacter || 0);
        this.apiPostTypeFileNoResponseAuth(`api/v0/Chat/ExtractFileTextFullStream?MaxLimitedCharacter=${maxLimitedCharacter}`, request.entity, callback, error);
      },
      // UploadCSV2Vector_Stream(request, callback, error) {
      //   let url =  `api/v0/GPTStream/UploadFile?modeltype=${request.modelType}&vectordbname=${request.vectordbName}&deleteAll=${request.deleteAll}&DetectLanguage=${request.DetectLanguage}&USER_CHANNEL=${request.USER_CHANNEL}&TopVDB=${request.topVDB}&FileName=${request.searchVectorDBName2}&FileId=${request.fileId}&cosine=${request.cosine}`;
      //   if(request.customCharacter != null){
      //     url += `&customCharacter=${request.customCharacter}`;
      //   }
      //   if (request.groupReplaceID != null) {
      //     url += `&groupReplaceID=${request.groupReplaceID}`;
      //   }
      //   if (request.MaxLimitedCharacter != null) {
      //     url += `&MaxLimitedCharacter=${request.MaxLimitedCharacter}`;
      //   }
      //   this.apiPostTypeFileNoResponseAuth(
      //     url,
      //     request.entity,
      //     callback,
      //     error
      //   );
      // },
      // UploadCSV2Vector_FullStream(request, callback, error) {
      //   let url =  `api/v0/GPTStream/UploadFileFullStream?modeltype=${request.modelType}&vectordbname=${request.vectordbName}&deleteAll=${request.deleteAll}&DetectLanguage=${request.DetectLanguage}&USER_CHANNEL=${request.USER_CHANNEL}&TopVDB=${request.topVDB}&FileName=${request.searchVectorDBName2}&FileId=${request.fileId}&cosine=${request.cosine}`;
      //   if(request.customCharacter != null){
      //     url += `&customCharacter=${request.customCharacter}`;
      //   }
      //   if (request.groupReplaceID != null) {
      //     url += `&groupReplaceID=${request.groupReplaceID}`;
      //   }
      //   if (request.MaxLimitedCharacter != null) {
      //     url += `&MaxLimitedCharacter=${request.MaxLimitedCharacter}`;
      //   }
      //   this.apiPostTypeFileNReFreshVector_FullStreamoResponseAuth(
      //     url,
      //     request.entity,
      //     callback,
      //     error
      //   );
      // },

      ReFreshVector_Stream(request, Config, callback, error) {
        this.apiPostAuthNoAlert_Config('api/v0/GPTStream/ReFreshVector_Stream', request, Config, callback, error);
      },
      ReFreshVector_FullStream(request, Config, Token, callback, error) {
        this.apiPostTokenText_Stream('api/v0/GPTStream/ReFreshVector_FullStream', Config, request, callback, error, Token);
      },
      // ReProcessedVector_Stream(request, callback, error) {
      //   this.apiPostAuth('api/v0/GPTStream/ReProcessed', request, callback, error);
      // },
      // ReProcessedVector_FullStream(request, callback, error) {
      //   this.apiPostAuth('api/v0/GPTStream/ReProcessedFullStream', request, callback, error);
      // },
      SaveEditVector_Stream(request, callback, error) {
        this.apiPostAuth(`api/v0/GPTStream/SaveEditVectorPrivateStream`, request, callback, error);
      },
      SaveEditVector_FullStream(request, Token, callback, error) {
        this.apiPostToken(`api/v0/GPTStream/SaveEditVectorPrivateFullStream`, request, callback, error, Token);
      },
      SaveAddVector_Stream(request, callback, error) {
        this.apiPostAuth(`api/v0/GPTStream/SaveAddVectorPrivateStream`, request, callback, error);
      },
      SaveAddVector_FullStream(request, Token, callback, error) {
        this.apiPostToken(`api/v0/GPTStream/SaveAddVectorPrivateFullStream`, request, callback, error, Token);
      },

      Delete_ListFilePM_ById_Stream(param, callback, error) {
        this.apiDeleteAuth(`api/v0/GPTStream/Delete_ListFilePM_ById_Stream?id=${param}`, callback, error);
      },
      Delete_ListFilePM_ById_FullStream(param, Token, callback, error) {
        this.apiDeleteToken(`api/v0/GPTStream/Delete_ListFilePM_ById_FullStream?id=${param}`, callback, error, Token);
      },
      DeleteVectorDBNameById_Stream(param, callback, error) {
        this.apiDeleteAuth(`api/v0/GPTStream/DeleteVectorDBNameById_Stream?id=${param}`, callback, error);
      },
      DeleteVectorDBNameById_FullStream(param, Token, callback, error) {
        this.apiDeleteToken(`api/v0/GPTStream/DeleteVectorDBNameById_FullStream?id=${param}`, callback, error, Token);
      },
      DeleteVectorDBNameByName_Stream(param, callback, error) {
        this.apiDeleteAuth(`api/v0/GPTStream/DeleteVectorDBNameByName_Stream?vectorDBName=${param.name}&FileId=${param.FileId}`, callback, error);
      },
      DeleteVectorDBNameByName_FullStream(param, Token, callback, error) {
        this.apiDeleteToken(`api/v0/GPTStream/DeleteVectorDBNameByName_FullStream?vectorDBName=${param.name}&FileId=${param.FileId}`, callback, error, Token);
      },
      SaveCosine_TopVDBStream(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStream/SaveCosine_TopVDBStream', request, callback, error);
      },
      SaveCosine_TopVDBFullStream(request, Token, callback, error) {
        this.apiPostToken('api/v0/GPTStream/SaveCosine_TopVDBFullStream', request, callback, error, Token);
      },
      SaveFileName_PrivateMemoryStream(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStream/SaveFileName_PrivateMemoryStream', request, callback, error);
      },
      SaveFileName_PrivateMemoryFullStream(request, Token, callback, error) {
        this.apiPostToken('api/v0/GPTStream/SaveFileName_PrivateMemoryFullStream', request, callback, error, Token);
      },
      CancelFileName_PrivateMemory(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStream/CancelFileName_PrivateMemory', request, callback, error);
      },
      GetListFile_PrivateMemoryStream(request, callback, error) {
        this.apiPostAuth('api/v0/GPTStream/GetListFile_PrivateMemoryStream', request, callback, error);
      },
      GetListFile_PrivateMemoryFullStream(request, Token, callback, error) {
        this.apiPostToken('api/v0/GPTStream/GetListFile_PrivateMemoryFullStream', request, callback, error, Token);
      },

      //#endregion

      // ExternalAPI
      GetCheckAnonymous(callback, error) {
        let request = {};
        this.apiPostAuth(`api/v0/ExternalAPI/GetCheckAnonymous`, request, callback, error);
      },

      GetModelFirst(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/GPTStream/GetModelFirst', request, callback, error);
      },

      //Report
      GetReportQuestion(request, callback, error) {
        this.apiPostAuth('api/v0/Report/ReportQuestionTop10', request, callback, error);
      },
      QuestionGenerate(request, callback, error) {
        this.apiPostAuth('api/v0/Report/QuestionGenerate', request, callback, error);
      },
      ReportUser(request, callback, error) {
        this.apiPostAuth('api/v0/Report/ReportUserTop10', request, callback, error);
      },
      ReportUserUse(request, callback, error) {
        this.apiPostAuth('api/v0/Report/ReportUserUse', request, callback, error);
      },
      ExportReportUserUse(request, callback, error) {
        this.apiPostTypeFileAuth('api/v0/Report/ExportReportUserUse', request, callback, error);
      },
      ReportTokenSum(request, callback, error) {
        this.apiPostAuth('api/v0/Report/TotalToken', request, callback, error);
      },
      ExportReportTokenSummation(request, callback, error) {
        this.apiPostTypeFileAuth('api/v0/Report/ExportReportTokenSummation', request, callback, error);
      },
      ReportPageToken(request, callback, error) {
        this.apiPostAuth('api/v0/Report/PageTokenReport', request, callback, error);
      },
      //APIToken

      GetAPITokenData(request, callback, error) {
        this.apiPostAuth(`api/v0/ApiToken/GetAPITokenData`, request, callback, error);
      },
      RenewAPIToken(request, callback, error) {
        this.apiPostAuth(`api/v0/ApiToken/RenewAPIToken`, request, callback, error);
      },
      CreateAPIToken(request, callback, error) {
        this.apiPostAuth(`api/v0/ApiToken/CreateAPIToken`, request, callback, error);
      },
      RevorkAPIToken(request, callback, error) {
        this.apiPostAuth(`api/v0/ApiToken/RevorkAPIToken`, request, callback, error);
      },

      // LiteLLM connector (SettingExternalConnector row action + CoreConfiguration test)
      LiteLLMStatus(request, callback, error) {
        this.apiPostAuth(`api/v0/LiteLLMConnector/Status`, request, callback, error);
      },
      LiteLLMInfo(request, callback, error) {
        this.apiPostAuth(`api/v0/LiteLLMConnector/Info`, request, callback, error);
      },
      LiteLLMKey(request, callback, error) {
        this.apiPostAuth(`api/v0/LiteLLMConnector/Key`, request, callback, error);
      },
      LiteLLMEnable(request, callback, error) {
        this.apiPostAuth(`api/v0/LiteLLMConnector/Enable`, request, callback, error);
      },
      LiteLLMSetActive(request, callback, error) {
        this.apiPostAuth(`api/v0/LiteLLMConnector/SetActive`, request, callback, error);
      },
      LiteLLMRegenerateKey(request, callback, error) {
        this.apiPostAuth(`api/v0/LiteLLMConnector/RegenerateKey`, request, callback, error);
      },
      LiteLLMUpdateLimits(request, callback, error) {
        this.apiPostAuth(`api/v0/LiteLLMConnector/UpdateLimits`, request, callback, error);
      },
      LiteLLMSyncModels(request, callback, error) {
        this.apiPostAuth(`api/v0/LiteLLMConnector/SyncModels`, request, callback, error);
      },
      LiteLLMTestConnection(request, callback, error) {
        this.apiPostAuth(`api/v0/LiteLLMConnector/TestConnection`, request, callback, error);
      },

      // ReleaseNote
      GetReleaseNote(callback, error) {
        this.apiGetAuth(`api/v0/ReleaseNote/GetReleaseNote`, callback, error);
      },

      // ReleaseNote Management (SuperAdmin)
      ReleaseNoteMgmtGetList(targetSystem, callback, error) {
        const qs = targetSystem ? `?targetSystem=${encodeURIComponent(targetSystem)}` : '';
        this.apiGetAuth(`api/v0/ReleaseNoteManagement/GetList${qs}`, callback, error);
      },
      ReleaseNoteMgmtGetById(releaseNoteId, callback, error) {
        this.apiGetAuth(`api/v0/ReleaseNoteManagement/GetById?releaseNoteId=${releaseNoteId}`, callback, error);
      },
      ReleaseNoteMgmtCreate(payload, callback, error) {
        this.apiPostAuth(`api/v0/ReleaseNoteManagement/Create`, payload, callback, error);
      },
      ReleaseNoteMgmtUpdate(payload, callback, error) {
        this.apiPostAuth(`api/v0/ReleaseNoteManagement/Update`, payload, callback, error);
      },
      ReleaseNoteMgmtPublish(releaseNoteId, callback, error) {
        this.apiPostAuth(`api/v0/ReleaseNoteManagement/Publish`, { releaseNoteId }, callback, error);
      },
      ReleaseNoteMgmtDelete(releaseNoteId, callback, error) {
        this.apiPostAuth(`api/v0/ReleaseNoteManagement/Delete`, { releaseNoteId }, callback, error);
      },
      ReleaseNoteMgmtGetConnectorCatalog(callback, error) {
        this.apiGetAuth(`api/v0/ReleaseNoteManagement/GetConnectorCatalog`, callback, error);
      },

      // Feedback
      FeedbackFromClient(request, callback, error) {
        let Token = request.token;
        this.apiPostToken(`api/v0/Feedback/FeedbackFromClient`, request.data, callback, error, Token);
      },
      DeleteFeedbackFromClient(request, callback, error) {
        let Token = request.token;
        this.apiPostToken(`api/v0/Feedback/DeleteFeedbackFromClient`, request.data, callback, error, Token);
      },
      GetTableReportFeedback(request, callback, error) {
        this.apiPostAuth(`api/v0/Feedback/GetTableReportFeedback`, request, callback, error);
      },
      ExportReportFeedback(request, callback, error) {
        this.apiPostTypeFileAuth(`api/v0/Feedback/ExportReportFeedback`, request, callback, error);
      },
      //#region ConfigTheme
      SaveConfigTheme(request, callback, error) {
        this.apiPostAuth(`api/v0/ConfigTheme/SaveConfigTheme`, request, callback, error);
      },
      UploadTheme(param, callback, error) {
        // URLSearchParams encodes values — a chatbot name containing & or # used to cut off the params after it
        const query = new URLSearchParams({
          ChatbotName: param.ChatbotName ?? '',
          ChatbotIcon: param.ChatbotIcon ?? '',
          FontColorTitle: param.FontColorTitle ?? '',
          chatColorTheme: param.chatColorTheme ?? '',
          NavbarBtnColorTheme: param.NavbarBtnColorTheme ?? '',
          NavbarTxtColorTheme: param.NavbarTxtColorTheme ?? '',
          setDefaultJson: param.setDefaultJson ?? '',
        });
        this.apiPostImportFileAuth(`api/v0/ConfigTheme/upload?${query.toString()}`, param.formData, { withCredentials: true }, callback, error);
      },
      GetImage(filename, callback, error) {
        this.apiGetBlobAuthData(`api/v0/ConfigTheme/GetImage/${filename}`, callback, error);
      },
      GetConfigThem(companyId, callback, error) {
        // Backward-compatible optional leading param (same pattern as GetConsentConfig/GetViewVersion below):
        // existing callers do GetConfigThem(cb, err) — detect that shape and shift args.
        if (typeof companyId === 'function') {
          error = callback;
          callback = companyId;
          companyId = null;
        }
        const url = companyId == null ? `api/v0/ConfigTheme/GetConfigThem` : `api/v0/ConfigTheme/GetConfigThem?companyId=${companyId}`;
        this.apiGetAuth(url, callback, error);
      },
      SetDefault(callback, error) {
        let request = {};
        this.apiPostAuth(`api/v0/ConfigTheme/setDefault`, request, callback, error);
      },
      SaveSnippetTheme(request, callback, error) {
        this.apiPostAuth(`api/v0/ConfigTheme/SaveSnippetTheme`, request, callback, error);
      },
      SaveSnippetFullTheme(request, callback, error) {
        this.apiPostAuth(`api/v0/ConfigTheme/SaveSnippetFullTheme`, request, callback, error);
      },
      UploadSnippetIcon(formData, callback, error) {
        this.apiPostImportFileAuth(`api/v0/ConfigTheme/UploadSnippetIcon`, formData, { withCredentials: true }, callback, error);
      },
      // Snippet V2 "อัปเดตข้อมูลล่าสุด" line — widget side (FullStreamReDesignV2). Public endpoint; NoAlert on
      // purpose: the line is optional, so a failure must stay silent (it just stays hidden). companyId null =
      // backend falls back to the cookie company (bare /FullStreamV2), same as GetConfigThem.
      GetSnippetLastUpdate(companyId, domainName, full, callback, error) {
        const params = new URLSearchParams();
        // domainName is what actually identifies the snippet (the backend resolves it against the
        // registered domains); companyId only separates several snippets sharing one domain.
        if (domainName) params.set('domainName', domainName);
        if (companyId != null && companyId !== '') params.set('companyId', companyId);
        params.set('full', full ? 'true' : 'false');
        this.apiGetAuthNoAlert(`api/v0/SnippetLastUpdate/GetLastUpdate?${params.toString()}`, callback, error);
      },
      // Admin side (SnippetSmallCustomization / SnippetFullCustomization) — the latest date of every
      // source, for the "what will Auto mode show" preview. Company comes from the cookie claim, and the
      // answer is company-level (no `full`). The SETTING is not fetched here: it arrives with
      // GetConfigThem and is saved by SaveSnippetTheme / SaveSnippetFullTheme like every snippet field.
      GetSnippetLastUpdateSources(callback, error) {
        this.apiGetAuth(`api/v0/SnippetLastUpdate/GetSources`, callback, error);
      },
      //#endregoion

      //#region ConsentConfig
      GetConsentConfig(targetSystem, callback, error) {
        if (typeof targetSystem === 'function') { error = callback; callback = targetSystem; targetSystem = 'backend'; }
        this.apiGetAuth(`api/v0/ConsentConfig/GetConsentConfig?targetSystem=${targetSystem || 'backend'}`, callback, error);
      },
      UpdateConsent(request, callback, error) {
        this.apiPostAuth('api/v0/ConsentConfig/UpdateConsent', request, callback, error);
      },
      GetViewVersion(targetSystem, callback, error) {
        if (typeof targetSystem === 'function') { error = callback; callback = targetSystem; targetSystem = 'backend'; }
        this.apiGetAuth(`api/v0/ConsentConfig/GetViewVersion?targetSystem=${targetSystem || 'backend'}`, callback, error);
      },
      GetConsentById(request, callback, error) {
        this.apiPostAuth(`api/v0/ConsentConfig/GetConsentById`, request, callback, error);
      },
      //#endregoion

      //#region PromptTemplate
      GetTableGroupPromptTemplate(callback, error) {
        this.apiPostAuth(`api/v0/GroupPromptTemplate/GetTableGroupPromptTemplate`, callback, error);
      },
      DeleteGroupPromptTemplate(id, callback, error) {
        this.apiDeleteAuth(`api/v0/GroupPromptTemplate/DeleteGroupPromptTemplate?id=${id}`, callback, error);
      },
      //#endregion

      //#region File Management
      DownloadChatImage(request, token, callback, error) {
        if (token) {
          this.apiPostImageAuth('api/v0/GPTStream/DownloadChatImage', request, callback, error, token);
        } else {
          this.apiPostImageAuth('api/v0/FileManager/DownloadChatImage', request, callback, error);
        }
      },
      GetTableFileManager(request, callback, error) {
        this.apiPostAuth('api/v0/FileManager/GetTableFileManager', request, callback, error);
      },
      DeleteFileManager(id, callback, error) {
        this.apiDeleteAuth(`api/v0/FileManager/DeleteFileManager?id=${id}`, callback, error);
      },
      DeleteAllFileManager(fileIds, callback, error) {
        this.apiDeleteFileAuth(`api/v0/FileManager/DeleteAllFileManager`, fileIds, callback, error);
      },
      GetViewFileManagerById(id, callback, error) {
        this.apiPostAuth(`api/v0/FileManager/GetViewFileManagerById?id=${id}`, null, callback, error);
      },
      //#endregion

      //#region SecurityGroup

      GetTableSecurityGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/SecurityGroup/GetTableSecurityGroup`, request, callback, error);
      },
      AddSecurityGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/SecurityGroup/AddSecurityGroup`, request, callback, error);
      },
      SyncDataEntraIDGroup(id, callback, error) {
        this.apiPostAuth(`api/v0/SecurityGroup/SyncDataEntraIDGroup?id=${id}`, null, callback, error);
      },
      GetTableUserGroupManagement(request, callback, error) {
        this.apiPostAuth(`api/v0/SecurityGroup/GetTableUserGroupManagement`, request, callback, error);
      },
      DeleteSecurityGroup(id, callback, error) {
        this.apiDeleteAuth(`api/v0/SecurityGroup/Delete?id=${id}`, callback, error);
      },
      GetTableSetupUserGroupManagement(request, callback, error) {
        this.apiPostAuth(`api/v0/SecurityGroup/GetTableSetupUserGroupManagement`, request, callback, error);
      },
      GetSetupByID(id, callback, error) {
        this.apiPostAuth(`api/v0/SecurityGroup/GetSetupByID?id=${id}`, null, callback, error);
      },
      DeleteUserByID(param, callback, error) {
        this.apiDeleteAuth(`api/v0/SecurityGroup/DeleteUserByID?id=${param.id}&GroupID=${param.groupid}`, callback, error);
      },
      UpdateUserGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/SecurityGroup/UpdateUserGroup`, request, callback, error);
      },
      AddUserIntoGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/SecurityGroup/AddUserIntoGroup`, request, callback, error);
      },
      //#endregion
      SendFileMessage(request, config, callback, error) {
        this.apiPostImportFileAuth(`api/v0/ChatHub/SendFileMessage?topic=${request.topic}&type=${request.type}`, request.files, config, callback, error);
      },

      // #region ShortcutMenu
      CreateShortcutMenuGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/ShortcutMenu/CreateShortcutMenuGroup`, request, callback, error);
      },
      UpdateShortcutMenuGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/ShortcutMenu/UpdateShortcutMenuGroup`, request, callback, error);
      },
      GetTableShortcutMenuGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/ShortcutMenu/GetTableShortcutMenuGroup`, request, callback, error);
      },
      DeleteShortcutMenuGroup(id, callback, error) {
        this.apiDeleteAuth(`api/v0/ShortcutMenu/DeleteShortcutMenuGroup?id=${id}`, callback, error);
      },
      GetShortcutMenuByID(param, callback, error) {
        this.apiGetAuth(`api/v0/ShortcutMenu/GetShortcutMenuByID?id=${param}`, callback, error);
      },
      GetDDShortcutMenu(callback, error) {
        this.apiGetAuth(`api/v0/ShortcutMenu/GetDDShortcutMenu`, callback, error);
      },

      // #region Product
      GetDDProductGroupList(callback, error) {
        this.apiGetAuth(`api/v0/Product/GetDDProductGroupList`, callback, error);
      },
      GetTableProductGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/Product/GetTableProductGroup`, request, callback, error);
      },
      GetProductDetailById(param, callback, error) {
        this.apiGetAuth(`api/v0/Product/GetProductDetailById?id=${param}`, callback, error);
      },
      CreateCategory(request, callback, error) {
        this.apiPostAuth(`api/v0/Product/CreateCategory`, request, callback, error);
      },
      CreateProduct(request, callback, error) {
        this.apiPostImportFile(`api/v0/Product/CreateProduct`, request, callback, error);
      },
      DeleteProductGroup(request, callback, error) {
        this.apiPostAuth(`api/v0/Product/DeleteProductGroup`, request, callback, error);
      },
      DeleteProduct(request, callback, error) {
        this.apiPostAuth(`api/v0/Product/DeleteProduct`, request, callback, error);
      },
      ReprocessVectorProduct(request, callback, error) {
        this.apiPostAuth(`api/v0/Product/ReprocessVectorProduct`, request, callback, error);
      },

      // SuperAdmin — Company Management
      SAGetCompanies(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminCompany/GetCompanies', request, callback, error);
      },
      SACreateCompany(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminCompany/CreateCompany', request, callback, error);
      },
      SAUpdateCompany(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminCompany/UpdateCompany', request, callback, error);
      },
      SADeleteCompany(id, callback, error) {
        this.apiDeleteAuth('api/v0/Company/DeleteCompany?id=' + id, callback, error);
      },
      SAForceSetOwnerPassword(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminCompany/ForceSetOwnerPassword', request, callback, error);
      },
      SADDLCompany(callback, error) {
        this.apiGetAuth('api/v0/SuperAdminCompany/DDLCompany', callback, error);
      },
      SADDLAllCompany(callback, error) {
        this.apiGetAuth('api/v0/SuperAdminCompany/DDLAllCompany', callback, error);
      },
      SAGetCompanyTypeBreakdown(callback, error) {
        this.apiGetAuth('api/v0/SuperAdminDashboard/CompanyTypeBreakdown', callback, error);
      },
      // 2026-10-03 sa-dashboard-filter-fix (F04/R17): breakdown scoped to the dashboard's APPLIED filter — body = the
      // dashboard request (only CompanyIds is used; null = every active company). The GET above stays for other callers.
      SAGetCompanyTypeBreakdownScoped(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/SuperAdminDashboard/CompanyTypeBreakdown', request, callback, error);
      },
      SAGetCompaniesWithType(callback, error) {
        this.apiGetAuth('api/v0/SuperAdminCompany/GetCompaniesWithType', callback, error);
      },
      SAGetCompanyById(id, callback, error) {
        this.apiGetAuth(`api/v0/SuperAdminCompany/GetCompanyById/${id}`, callback, error);
      },
      // สิทธิ์เข้าหน้า External Connector / Plugin Common Channel ของผู้ดูแลบริษัท (SuperAdmin เปิด/ปิดรายบริษัท)
      // data = [{ pageKey, isAllowed }] · Save body = { companyId, pages: [{ pageKey, isAllowed }] }
      SAGetCompanyPagePermissions(companyId, callback, error) {
        this.apiGetAuth(`api/v0/CompanyPagePermission/GetByCompany/${companyId}`, callback, error);
      },
      SASaveCompanyPagePermissions(request, callback, error) {
        this.apiPostAuth('api/v0/CompanyPagePermission/Save', request, callback, error);
      },
      // หน้าที่ผู้ใช้ปัจจุบันเข้าได้ในบริษัทนี้ — data.allowedPages (NavBar ใช้ซ่อนเมนู · router ใช้ utils/companyPagePermission.js)
      GetMyCompanyPages(callback, error) {
        this.apiGetAuthNoAlert('api/v0/CompanyPagePermission/MyPages', callback, error);
      },

      // ===== SuperAdmin Document (Knowledge ข้ามทุกบริษัท) =====
      SAGetDocuments(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/GetDocuments', request, callback, error);
      },
      SAGetDocumentDetail(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/GetDocumentDetail', request, callback, error);
      },
      SAUploadDocument(request, callback, error) {
        let url = `api/v0/SuperAdminKnowledge/UploadDocument?companyId=${request.companyId}&vectorDBName=${encodeURIComponent(request.vectorDBName)}&modelType=${request.modelType}&deleteAll=${request.deleteAll}&detectLanguage=${request.detectLanguage}&maxLimitedCharacter=${request.maxLimitedCharacter}&personalInformation=${request.personalInformation}`;
        if (request.customCharacter != null) {
          url += `&customCharacter=${encodeURIComponent(request.customCharacter)}`;
        }
        if (request.groupReplaceID != null) {
          url += `&groupReplaceID=${encodeURIComponent(request.groupReplaceID)}`;
        }
        this.apiPostTypeFileNoResponseAuth(url, request.entity, callback, error);
      },
      SAUploadExisting(request, callback, error) {
        let url = `api/v0/SuperAdminKnowledge/UploadExisting?vectorDBName=${encodeURIComponent(request.vectorDBName)}&modelType=${request.modelType}&deleteAll=${request.deleteAll}&detectLanguage=${request.detectLanguage}&maxLimitedCharacter=${request.maxLimitedCharacter}&personalInformation=${request.personalInformation}`;
        if (request.companyId != null) {
          url += `&companyId=${request.companyId}`;
        }
        if (request.customCharacter != null) {
          url += `&customCharacter=${encodeURIComponent(request.customCharacter)}`;
        }
        if (request.groupReplaceID != null) {
          url += `&groupReplaceID=${encodeURIComponent(request.groupReplaceID)}`;
        }
        this.apiPostTypeFileNoResponseAuth(url, request.entity, callback, error);
      },
      SAAddChunk(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/AddChunk', request, callback, error);
      },
      SAEditChunk(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/EditChunk', request, callback, error);
      },
      SADeleteChunk(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/DeleteChunk', request, callback, error);
      },
      SADeleteDocument(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/DeleteDocument', request, callback, error);
      },
      SAExportDocument(request, callback, error) {
        this.apiPostTypeFileAuth('api/v0/SuperAdminKnowledge/ExportDocument', request, callback, error);
      },
      SAReprocessDocument(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/Reprocess', request, callback, error);
      },
      SAVectorOnOff(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/VectorOnOff', request, callback, error);
      },
      SAUpdateDocumentPersonalInfo(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/UpdatePersonalInformation', request, callback, error);
      },
      // Document Version Cycle — หน้ารอบยืนยันเวอร์ชัน (Super Admin)
      SAGetDocumentVersions(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/GetDocumentVersions', request, callback, error);
      },
      // request: { items: [{ companyId, vectorDBName }] } → data: { updated, notFound: [] }
      SAUpdateDocumentVersion(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/UpdateDocumentVersion', request, callback, error);
      },
      // request: { companyId, vectorDBName, versionStampEnabled: true|false|null } → data: bool
      SAUpdateVersionStamp(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/UpdateVersionStamp', request, callback, error);
      },
      SAGetVersionHistory(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/GetVersionHistory', request, callback, error);
      },
      SAGetVersionDetail(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/GetVersionDetail', request, callback, error);
      },
      SARestoreVersion(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/RestoreVersion', request, callback, error);
      },
      SADuplicateDocument(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/Duplicate', request, callback, error);
      },
      SAReplaceDocument(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminKnowledge/Replace', request, callback, error);
      },

      // ===== LLM Group (CR#09) =====
      SAGetLLMGroups(request, callback, error) {
        this.apiPostAuth('api/v0/LLMGroup/GetGroups', request, callback, error);
      },
      SAGetLLMGroupById(request, callback, error) {
        this.apiPostAuth('api/v0/LLMGroup/GetGroupById', request, callback, error);
      },
      SAGetAssignableModelData(callback, error) {
        this.apiGetAuth('api/v0/LLMGroup/GetAssignableModelData', callback, error);
      },
      SACreateLLMGroup(request, callback, error) {
        this.apiPostAuth('api/v0/LLMGroup/CreateGroup', request, callback, error);
      },
      SAUpdateLLMGroup(request, callback, error) {
        this.apiPostAuth('api/v0/LLMGroup/UpdateGroup', request, callback, error);
      },
      SADeleteLLMGroup(request, callback, error) {
        this.apiPostAuth('api/v0/LLMGroup/DeleteGroup', request, callback, error);
      },
      SASetDefaultLLMGroup(request, callback, error) {
        this.apiPostAuth('api/v0/LLMGroup/SetDefaultGroup', request, callback, error);
      },
      SAAssignLLMGroup(request, callback, error) {
        this.apiPostAuth('api/v0/LLMGroup/AssignGroupToCompany', request, callback, error);
      },
      SAAssignCompaniesToGroup(request, callback, error) {
        this.apiPostAuth('api/v0/LLMGroup/AssignCompaniesToGroup', request, callback, error);
      },
      SAGetLLMGroupAssignments(request, callback, error) {
        this.apiPostAuth('api/v0/LLMGroup/GetCompanyAssignments', request, callback, error);
      },
      SAGetOutOfGroupConfigs(request, callback, error) {
        this.apiPostAuth('api/v0/LLMGroup/GetOutOfGroupConfigs', request, callback, error);
      },
      SARemediateConfig(request, callback, error) {
        this.apiPostAuth('api/v0/LLMGroup/RemediateConfig', request, callback, error);
      },

      // SA — CompanyWorkgroup Management
      SAGetWorkgroupTypes(callback, error) {
        this.apiGetAuth('api/v0/SuperAdminCompanyWorkgroup/GetWorkgroupTypes', callback, error);
      },
      SACreateWorkgroupType(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminCompanyWorkgroup/CreateWorkgroupType', request, callback, error);
      },
      SAUpdateWorkgroupType(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminCompanyWorkgroup/UpdateWorkgroupType', request, callback, error);
      },
      SADeleteWorkgroupType(id, callback, error) {
        this.apiDeleteAuth(`api/v0/SuperAdminCompanyWorkgroup/DeleteWorkgroupType/${id}`, callback, error);
      },
      SAGetWorkgroupTree(callback, error) {
        this.apiGetAuth('api/v0/SuperAdminCompanyWorkgroup/GetWorkgroupTree', callback, error);
      },
      SAGetWorkgroupsByCompany(companyId, callback, error) {
        this.apiGetAuth(`api/v0/SuperAdminCompanyWorkgroup/GetWorkgroupsByCompany/${companyId}`, callback, error);
      },
      SAUpsertWorkgroup(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminCompanyWorkgroup/UpsertWorkgroup', request, callback, error);
      },
      SADeleteWorkgroup(id, callback, error) {
        this.apiDeleteAuth(`api/v0/SuperAdminCompanyWorkgroup/DeleteWorkgroup/${id}`, callback, error);
      },
      SAReorderWorkgroups(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminCompanyWorkgroup/ReorderWorkgroups', request, callback, error);
      },

      // SA — OrganizationStructure (สร้าง Company + Workgroup ในครั้งเดียว)
      SAGetOrganizationTree(filterHierarchyLevel, callback, error) {
        const query = filterHierarchyLevel == null ? '' : `?filterHierarchyLevel=${filterHierarchyLevel}`;
        this.apiGetAuth(`api/v0/SuperAdminOrganizationStructure/GetOrganizationTree${query}`, callback, error);
      },
      SACreateMinistry(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminOrganizationStructure/CreateMinistry', request, callback, error);
      },
      SACreateUnit(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminOrganizationStructure/CreateUnit', request, callback, error);
      },
      SAUpdateOrgNode(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminOrganizationStructure/UpdateNode', request, callback, error);
      },
      SADeleteOrganizationNode(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminOrganizationStructure/DeleteOrganizationNode', request, callback, error);
      },
      SADeleteOrgNode(workgroupId, callback, error) {
        this.apiDeleteAuth(`api/v0/SuperAdminOrganizationStructure/DeleteNode/${workgroupId}`, callback, error);
      },
      SAUnlinkWorkgroup(workgroupId, callback, error) {
        this.apiDeleteAuth(`api/v0/SuperAdminOrganizationStructure/UnlinkWorkgroup/${workgroupId}`, callback, error);
      },

      // SA — User Management (SuperAdmin Users in User table)
      SAGetTableUserManagement(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminUser/GetTable', request, callback, error);
      },
      SACreateUser(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminUser/Create', request, callback, error);
      },
      SAUpdateUser(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminUser/Update', request, callback, error);
      },
      SADeleteUser(id, callback, error) {
        this.apiDeleteAuth(`api/v0/SuperAdminUser/Delete/${id}`, callback, error);
      },
      SASaveChangePassword(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminUser/ChangePassword', request, callback, error);
      },
      SASaveChangePasswordProfile(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminUser/ChangePasswordProfile', request, callback, error);
      },

      // Web Content — Landing Page (unified 4-panel editor)
      WebContentLandingPageAll(callback, error) {
        this.apiGetAuth('api/v0/WebContent/LandingPage/All', callback, error);
      },
      WebContentLandingPageUpdate(panelKey, body, callback, error) {
        this.apiPostAuth(`api/v0/WebContent/LandingPage/Update/${panelKey}`, body, callback, error);
      },
      WebContentLandingPageReset(panelKey, callback, error) {
        this.apiPostAuth(`api/v0/WebContent/LandingPage/Reset/${panelKey}`, null, callback, error);
      },
      WebContentLandingPageCurrent(callback, error) {
        this.apiGet('api/v0/WebContent/LandingPage/Current', callback, error);
      },

      // Web Content — Banner / Popup (gallery + global config)
      WebContentBannerPopupAll(callback, error) {
        this.apiGetAuth('api/v0/WebContent/BannerPopup/All', callback, error);
      },
      WebContentBannerPopupUploadImage(formData, callback, error) {
        this.apiPostImportFileAuth('api/v0/WebContent/BannerPopup/Upload', formData, { withCredentials: true }, callback, error);
      },
      WebContentBannerPopupCreate(body, callback, error) {
        this.apiPostAuth('api/v0/WebContent/BannerPopup/Create', body, callback, error);
      },
      WebContentBannerPopupUpdate(id, body, callback, error) {
        this.apiPostAuth(`api/v0/WebContent/BannerPopup/Update/${id}`, body, callback, error);
      },
      WebContentBannerPopupSetActive(id, callback, error) {
        this.apiPostAuth(`api/v0/WebContent/BannerPopup/SetActive/${id}`, null, callback, error);
      },
      WebContentBannerPopupDelete(id, callback, error) {
        this.apiPostAuth(`api/v0/WebContent/BannerPopup/Delete/${id}`, null, callback, error);
      },
      WebContentBannerPopupConfig(body, callback, error) {
        this.apiPostAuth('api/v0/WebContent/BannerPopup/Config', body, callback, error);
      },
      WebContentBannerPopupCurrent(callback, error) {
        // Public, credential-free GET. The endpoint is embeddable on any customer site,
        // so its CORS policy returns a wildcard origin ('*') which cannot be combined with
        // credentialed requests. Force withCredentials:false (overriding the global axios
        // default) so the wildcard response is accepted by the browser.
        const instance = axios.create({
          withCredentials: false,
          headers: { Accept: 'application/json' },
        });
        instance
          .get(this.urlApi + 'api/v0/WebContent/BannerPopup/Current')
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            return error(error_);
          });
      },

      // FullStream personalized welcome card — company-level Persona/GDX entitlement gate.
      appendFullStreamContextParams(params) {
        const current = new URLSearchParams(globalThis.location.search || '');
        for (const key of ['domainName', 'chk_token', 'companyId']) {
          const value = current.get(key) || current.get(key.toLowerCase());
          if (value && !params.has(key)) params.set(key, value);
        }
        // companyId fallback (URL → Vuex store), mirroring welfareProbeCompanyId. A SuperAdmin
        // previewing a snippet has no own companyId; the backend honors this param ONLY for a
        // SuperAdmin (tenant isolation) to scope the Persona entitlement gate to the previewed company.
        if (!params.has('companyId')) {
          const storeCompanyId = Number(store?.state?.companyId) || 0;
          if (storeCompanyId > 0) params.set('companyId', String(storeCompanyId));
        }
        return params;
      },
      GetPersonaEnabled(token, callback, error) {
        const params = new URLSearchParams();
        this.appendFullStreamContextParams(params);
        const query = params.toString() ? `?${params.toString()}` : '';
        this.apiGetToken(`api/v0/Persona/enabled${query}`, callback, error, token);
      },
      // FullStream personalized welcome card — Persona air-quality source (login-only, lat/lon optional)
      GetPersonaAir(lat, lon, token, callback, error) {
        const params = new URLSearchParams();
        if (lat != null && lat !== '') params.set('lat', lat);
        if (lon != null && lon !== '') params.set('lon', lon);
        this.appendFullStreamContextParams(params);
        const query = params.toString() ? `?${params.toString()}` : '';
        this.apiGetToken(`api/v0/Persona/air${query}`, callback, error, token);
      },
      // FullStream personalized welcome card — Persona weather source from Persona API Setting.
      GetPersonaWeather(lat, lon, token, callback, error) {
        const params = new URLSearchParams();
        if (lat != null && lat !== '') params.set('lat', lat);
        if (lon != null && lon !== '') params.set('lon', lon);
        this.appendFullStreamContextParams(params);
        const query = params.toString() ? `?${params.toString()}` : '';
        this.apiGetToken(`api/v0/Persona/weather${query}`, callback, error, token);
      },
      // S16-P3: Persona POST ทุกเส้นส่ง context ผ่าน body ล้วน (payload-only — backend เลิกอ่าน
      // query/Referer ในเส้น POST แล้ว) — mirror ของ appendFullStreamContextParams (query, ใช้กับ GET)
      fullStreamContextBody() {
        const current = new URLSearchParams(globalThis.location.search || '');
        const ctx = {};
        const domainName = current.get('domainName') || current.get('domainname');
        if (domainName) ctx.domainName = domainName;
        const chkToken = current.get('chk_token');
        if (chkToken) ctx.chkToken = chkToken;
        // companyId fallback (URL → Vuex store) — backend honor เฉพาะ SuperAdmin probe / snippet resolve
        const companyId = Number(current.get('companyId') || current.get('companyid')) || Number(store?.state?.companyId) || 0;
        if (companyId > 0) ctx.companyId = companyId;
        return ctx;
      },
      // FullStream welcome card — Persona weather daily (POST, mapped WeatherResult — S16-P3)
      PostPersonaWeather(request, token, callback, error) {
        this.apiPostAuthNoAlertToken('api/v0/Persona/weather', { ...this.fullStreamContextBody(), ...request }, callback, error, token);
      },
      // FullStream welcome card — Persona current temperature (POST, mapped WeatherCurrentResult — S16-P3)
      PostPersonaWeatherHourly(request, token, callback, error) {
        this.apiPostAuthNoAlertToken('api/v0/Persona/weather/hourly', { ...this.fullStreamContextBody(), ...request }, callback, error, token);
      },
      // FullStream welcome card — Persona air nearest-station (POST, mapped AirQualityResult — S16-P3)
      PostPersonaAir(request, token, callback, error) {
        this.apiPostAuthNoAlertToken('api/v0/Persona/air', { ...this.fullStreamContextBody(), ...request }, callback, error, token);
      },
      // FullStream personalized welcome card — current weather/temperature legacy fallback (login-only, lat/lon optional)
      GetWeather(lat, lon, callback, error) {
        const params = [];
        if (lat != null && lat !== '') params.push(`lat=${encodeURIComponent(lat)}`);
        if (lon != null && lon !== '') params.push(`lon=${encodeURIComponent(lon)}`);
        const query = params.length ? `?${params.join('&')}` : '';
        this.apiGetToken(`api/v0/Temperature/GetCurrentWeather${query}`, callback, error, Token);
      },
      // FullStream welcome card — query config-driven GDX welfare recommendations by CitizenId
      LogWelfareQuery(request, Token, callback, error) {
        this.apiPostAuthNoAlertToken('api/v0/Welfare/LogWelfareQuery', request, callback, error, Token);
      },
      // FullStream welcome card — fire-and-forget card interaction tracking (dismiss / view_rights)
      TrackPersonalizeCardInteraction(request, Token, callback, error) {
        this.apiPostAuthNoAlertToken('api/v0/Welfare/TrackPersonalizeCardInteraction', request, callback, error, Token);
      },

      UploadSingleFile(request, callback, error) {
        this.apiPostImportFileAuth('api/v0/UploadFile/UploadSingleFile', request, {}, callback, error);
      },

      // SA — Package Management
      SAGetTablePackage(request, callback, error) {
        this.apiPostAuth('api/v0/Package/GetTablePackage', request, callback, error);
      },
      SACreatePackage(request, callback, error) {
        this.apiPostAuth('api/v0/Package/CreatePackage', request, callback, error);
      },
      SAUpdatePackage(request, callback, error) {
        this.apiPostAuth('api/v0/Package/UpdatePackage', request, callback, error);
      },
      SADeletePackage(request, callback, error) {
        this.apiPostAuth('api/v0/Package/DeletePackage', request, callback, error);
      },
      SADDLPackage(callback, error) {
        this.apiGetAuth('api/v0/Package/DDLPackage', callback, error);
      },

      // SA — Sale Management
      SAGetTableSale(request, callback, error) {
        this.apiPostAuth('api/v0/Sale/GetTableSale', request, callback, error);
      },
      SACreateSale(request, callback, error) {
        this.apiPostAuth('api/v0/Sale/CreateSale', request, callback, error);
      },
      SAUpdateSale(request, callback, error) {
        this.apiPostAuth('api/v0/Sale/UpdateSale', request, callback, error);
      },
      SADeleteSale(id, callback, error) {
        this.apiDeleteAuth(`api/v0/Sale/DeleteSale?id=${id}`, callback, error);
      },
      SADDLSale(callback, error) {
        this.apiGetAuth('api/v0/Sale/DDLSale', callback, error);
      },

      // SA — Subscription Management
      SAGetTableSubscription(request, callback, error) {
        this.apiPostAuth('api/v0/Subscription/GetTableSubscription', request, callback, error);
      },
      SACreateSubscription(request, callback, error) {
        this.apiPostAuth('api/v0/Subscription/CreateSubscription', request, callback, error);
      },
      SAUpdateSubscription(request, callback, error) {
        this.apiPostAuth('api/v0/Subscription/UpdatePeriodAndTokenSubscription', request, callback, error);
      },
      SAGetProjectSubscription(callback, error) {
        this.apiGetAuth('api/v0/Subscription/GetProjectSubscription', callback, error);
      },
      // card Project (Company Details tab) — ยอด chat/processing/total จาก dashboard rollup (แยกจาก GetProjectSubscription ที่คืน limit/counter)
      SAGetProjectTokenUsage(callback, error) {
        this.apiGetAuth('api/v0/SuperAdminDashboard/ProjectTokenUsage', callback, error);
      },
      SAUpdateProjectSubscription(request, callback, error) {
        this.apiPostAuth('api/v0/Subscription/UpdateProjectSubscription', request, callback, error);
      },
      SACancelSubscription(request, callback, error) {
        this.apiPostAuth('api/v0/Subscription/CancelSubscription', request, callback, error);
      },
      SARenewSubscription(request, callback, error) {
        this.apiPostAuth('api/v0/Subscription/RenewSubscription', request, callback, error);
      },
      SAGetHistorypayment(request, callback, error) {
        this.apiPostAuth('api/v0/Subscription/GetHistorypayment', request, callback, error);
      },
      SAUnsubscribe(id, callback, error) {
        this.apiDeleteAuth(`api/v0/Subscription/Unsubscribe?id=${id}`, callback, error);
      },
      SATemplatePackages(callback, error) {
        this.apiGetAuth('api/v0/Subscription/TemplatePackages', callback, error);
      },

      // SA — Event Log
      SAGetEventLog(request, callback, error) {
        this.apiPostAuth('api/v0/Log/EventLogShow', request, callback, error);
      },

      // SA — Setup / Core Configuration
      SAGetSetupGeneral(callback, error) {
        this.apiGetAuth('api/v0/Setup/OverviewGeneral', callback, error);
      },
      SAUpdateSetupGeneral(request, callback, error) {
        this.apiPostAuth('api/v0/Setup/UpdateGeneral', request, callback, error);
      },
      SAApplySetup(request, callback, error) {
        this.apiPostAuth('api/v0/Setup/Apply', request, callback, error);
      },
      GetPdfToMarkdownIframeSettings(callback, error) {
        this.apiGetAuth('api/v0/Setup/PdfToMarkdownIframeSettings', callback, error);
      },
      // pdf2md SSO (แบบ KnowledgeGraph/GetEmbedToken ของ NEXA) — หน้า Tool ใส่ token ใน fragment ของ iframe
      // ไม่มี alert/ไม่พาไปหน้า login: ขอไม่ได้ = เปิด iframe แบบเดิม (pdf2md ที่ไม่ได้เปิด SSO ใช้ได้ตามเดิม)
      GetPdfToMarkdownEmbedToken(callback, error) {
        this.apiPostAuthNoAlert('api/v0/PdfToMarkdown/EmbedToken', {}, callback, error);
      },
      // PDF ใน wizard "สร้าง AI Assistant ของคุณ" (แปลงผ่าน pdf2md แล้วนำเข้าเป็นเอกสาร MaxCharacter)
      PdfConvertStart(formData, callback, error) {
        this.apiPostTypeFileNoResponseAuth('api/v0/PdfToMarkdown/ConvertStart', formData, callback, error);
      },
      PdfConvertStatus(request, callback, error) {
        this.apiPostAuth('api/v0/PdfToMarkdown/ConvertStatus', request, callback, error);
      },
      PdfConvertFinish(request, callback, error) {
        this.apiPostAuth('api/v0/PdfToMarkdown/ConvertFinish', request, callback, error);
      },
      // ยกเลิกตอนลบไฟล์/ปิด wizard — best effort ไม่มี alert
      PdfConvertCancel(request, callback, error) {
        this.apiPostAuthNoAlert('api/v0/PdfToMarkdown/ConvertCancel', request, callback, error);
      },
      // wizard เด้งเองครั้งแรกที่ Admin เข้าหน่วยงาน (ตาราง mas.AssistantWizardOnboarding — หน่วยงาน/ผู้ใช้อ่านจาก claim ฝั่ง backend)
      // เรียกเงียบ ๆ ตอนโหลดหน้า: ล้ม = แค่ไม่เด้ง ไม่มี alert และไม่พาไปหน้า login
      AssistantWizardOnboardingStatus(callback, error) {
        this.apiPostAuthNoAlert('api/v0/AssistantWizard/OnboardingStatus', {}, callback, error);
      },
      AssistantWizardOnboardingShown(callback, error) {
        this.apiPostAuthNoAlert('api/v0/AssistantWizard/OnboardingShown', {}, callback, error);
      },

      // SA: Search Pin (FusionSearch) — หน้า "ปักหมุดผลค้นหา MiniApp" (/SuperAdmin/SearchPin) + ปุ่มทดสอบใน Core Configuration
      // CF backend เรียก Admin API ของ FusionSearch แทน (JWT อายุสั้น) · ทุก action ตอบ ResponseModel (HTTP 200)
      // ผลไม่สำเร็จ = status 1/2 + data.code (not_configured, auth_failed, unreachable, validation, conflict, ...) — หน้าจัดการเอง ไม่มี alert กลาง
      SASearchPinStatus(callback, error) {
        this.apiGetAuth('api/v0/SuperAdminSearchPin/Status', callback, error);
      },
      SASearchPinGetRules(callback, error) {
        this.apiGetAuth('api/v0/SuperAdminSearchPin/Rules', callback, error);
      },
      // request = { keywords[], match: 'exact'|'contains', pins: [{ miniAppId }], enabled, note }
      SASearchPinCreateRule(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminSearchPin/CreateRule', request, callback, error);
      },
      // request = rule body + { id, updatedAt } (updatedAt ตามที่โหลดมา — ถ้ามีคนแก้ไปก่อนได้ data.code 'conflict')
      SASearchPinUpdateRule(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminSearchPin/UpdateRule', request, callback, error);
      },
      // request = { id }
      SASearchPinDeleteRule(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminSearchPin/DeleteRule', request, callback, error);
      },
      // ค้นแอปจากชื่อไทย / อังกฤษ / รหัส (limit 1–50)
      SASearchPinSearchApps(q, limit, callback, error) {
        const query = new URLSearchParams({ q: q || '', limit: String(limit || 20) }).toString();
        this.apiGetAuth(`api/v0/SuperAdminSearchPin/Apps?${query}`, callback, error);
      },
      // request = { query, lang?, filter?, filterId?, score?, limit?, draftRule? }
      SASearchPinPreview(request, callback, error) {
        this.apiPostAuth('api/v0/SuperAdminSearchPin/Preview', request, callback, error);
      },
      SASearchPinGetPipeline(callback, error) {
        this.apiGetAuth('api/v0/SuperAdminSearchPin/Pipeline', callback, error);
      },
      SASearchPinTriggerPipeline(callback, error) {
        this.apiPostAuth('api/v0/SuperAdminSearchPin/TriggerPipeline', {}, callback, error);
      },

      // GDX / Persona API settings (Product Admin → Persona API Setting)
      SAGetGdxSettings(callback, error) {
        this.apiGetAuth('api/v0/Setup/GdxSettings', callback, error);
      },
      SAUpdateGdxSettings(request, callback, error) {
        this.apiPostAuth('api/v0/Setup/GdxSettings', request, callback, error);
      },
      SAGetGdxConfig(callback, error) {
        this.apiGetAuth('api/v0/Gdx/config', callback, error);
      },
      SAUpsertGdxConfig(request, callback, error) {
        this.apiPostAuth('api/v0/Gdx/config', request, callback, error);
      },
      SADeleteGdxConfig(id, callback, error) {
        this.apiDeleteAuth(`api/v0/Gdx/config/${id}`, callback, error);
      },
      // GDX Service master (sy.GdxService) — per-service coverage + cache (SuperAdmin only)
      SAGetGdxServices(callback, error) {
        this.apiGetAuth('api/v0/Gdx/services', callback, error);
      },
      SAUpsertGdxService(request, callback, error) {
        this.apiPostAuth('api/v0/Gdx/services', request, callback, error);
      },
      SADeleteGdxService(id, callback, error) {
        this.apiDeleteAuth(`api/v0/Gdx/services/${id}`, callback, error);
      },
      // §15 per-company Persona entitlement (SuperAdmin only)
      SAGetGdxEntitlements(callback, error) {
        this.apiGetAuth('api/v0/Gdx/entitlement', callback, error);
      },
      SAUpsertGdxEntitlement(request, callback, error) {
        this.apiPostAuth('api/v0/Gdx/entitlement', request, callback, error);
      },
      // Persona multi-source (air / weather / DOPA) — "Data Sources" card
      SAGetPersonaSources(callback, error) {
        this.apiGetAuth('api/v0/Persona/sources', callback, error);
      },
      SAUpsertPersonaSource(request, callback, error) {
        this.apiPostAuth('api/v0/Persona/sources', request, callback, error);
      },
      // POST /Persona/air — admin self-test (S16-P3/P4): เส้นเดียวกับ consumer (mapped AirQualityResult,
      // server เลือกสถานีใกล้สุดจาก lat/lon). adminTest ต้องอยู่ใน body (POST เลิกอ่าน ?adminTest=1) +
      // cookie แอดมิน (apiPostAuth withCredentials) → ข้าม entitlement gate. ⚠️S16-P4: caller (PersonaApiSetting)
      // ต้องส่ง lat/lon จริง — ไม่ส่งจะได้ feed ทุกสถานี (การ์ดสถานีใกล้สุดพัง), ส่งครึ่งเดียว → 400
      SATestPersonaAir(lat, lon, callback, error) {
        const body = { adminTest: true };
        if (lat !== undefined && lat !== null) body.lat = lat;
        if (lon !== undefined && lon !== null) body.lon = lon;
        this.apiPostAuth('api/v0/Persona/air', body, callback, error);
      },
      // POST /Persona/weather/hourly — admin self-test (S16-P3): อุณหภูมิปัจจุบัน (mapped
      // WeatherCurrentResult) มาตรฐานเดียวกับที่หน้าแชท FullStream ใช้ (mock in dev; real needs a TMD key)
      SATestPersonaWeather(lat, lon, callback, error) {
        const body = { adminTest: true };
        if (lat !== undefined && lat !== null) body.lat = lat;
        if (lon !== undefined && lon !== null) body.lon = lon;
        this.apiPostAuth('api/v0/Persona/weather/hourly', body, callback, error);
      },

      // SA — LLM Management
      SAGetDDLProvider(callback, error) {
        this.apiGetAuth('api/v0/ProviderData/GetDDLProviderData', callback, error);
      },
      SAGetModelData(request, callback, error) {
        this.apiPostAuth('api/v0/ModelData/GetModelData', request, callback, error);
      },
      SACreateModelData(request, callback, error) {
        this.apiPostAuth('api/v0/ModelData/CreateModelData', request, callback, error);
      },
      SAUpdateModelData(request, callback, error) {
        this.apiPostAuth('api/v0/ModelData/UpdateModelData', request, callback, error);
      },
      SADeleteModelData(request, callback, error) {
        this.apiPostAuth('api/v0/ModelData/DeleteModelData', request, callback, error);
      },

      // SA — Consent Configuration
      SAGetConsentConfig(targetSystem, callback, error) {
        if (typeof targetSystem === 'function') { error = callback; callback = targetSystem; targetSystem = 'backend'; }
        this.apiGetAuth(`api/v0/ConsentConfig/GetConsentConfigAdmin?targetSystem=${targetSystem || 'backend'}`, callback, error);
      },
      SAUpdateConsent(request, callback, error) {
        const body = { targetSystem: 'backend', ...request };
        this.apiPostAuth('api/v0/ConsentConfig/UpdateConsent', body, callback, error);
      },
      SAGetConsentById(request, callback, error) {
        const body = { targetSystem: 'backend', ...request };
        this.apiPostAuth('api/v0/ConsentConfig/GetConsentById', body, callback, error);
      },
      // Two-Audience Terms — Frontend Bearer endpoints
      GetConsentFrontend(token, callback, error) {
        this.apiPostToken('api/v0/GPTStream/GetConsentFrontend', {}, callback, error, token);
      },
      SaveAgreeConsentFrontend(request, token, callback, error) {
        this.apiPostToken('api/v0/GPTStream/SaveAgreeConsentFrontend', request, callback, error, token);
      },
      GetPublicConsentFrontend(callback, error) {
        this.apiGet('api/v0/ConsentConfig/GetPublicConsent?targetSystem=frontend', callback, error);
      },
      // Privacy Notice (audience 'privacy') — anonymous, used by /Landing/Privacy
      GetPublicConsentPrivacy(callback, error) {
        this.apiGet('api/v0/ConsentConfig/GetPublicConsent?targetSystem=privacy', callback, error);
      },
      // Snippet Terms & Conditions (audience 'snippet') — anonymous, used by /Landing/Terms
      GetPublicConsentSnippet(callback, error) {
        this.apiGet('api/v0/ConsentConfig/GetPublicConsent?targetSystem=snippet', callback, error);
      },
      // Backend Terms & Conditions (audience 'backend') — popup อ่านอย่างเดียวจากเมนู profile ฝั่ง admin
      GetPublicConsentBackend(callback, error) {
        this.apiGet('api/v0/ConsentConfig/GetPublicConsent?targetSystem=backend', callback, error);
      },

      setCookie(name, value) {
        document.cookie = name + '=' + (value || '');
      },
      getCookie(name) {
        let nameEQ = name + '=';
        let ca = document.cookie.split(';');
        for (const rawCookie of ca) {
          let c = rawCookie;
          while (c.startsWith(' ')) c = c.substring(1, c.length);
          if (c.startsWith(nameEQ)) return c.substring(nameEQ.length, c.length);
        }
        return null;
      },
      eraseCookie(name) {
        document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      },

      ClearUserInfo() {
        // หน้า widget ลบเฉพาะ cookie ของ widget เอง — cookie อื่นใน jar เดียวกันเป็นของ portal ที่ login อยู่
        // (embed ใต้ site เดียวกันอย่าง *.dga.or.th ลบ cookie ของ portal ได้จริง = portal หลุดไปด้วย)
        const cookieNames = isSnippetPage()
          ? SNIPPET_COOKIE_NAMES
          : Object.keys(Cookies.get()).filter((cookieName) => cookieName != 'rememberMe');
        for (const cookieName of cookieNames) {
          Cookies.remove(cookieName);
        }

        const sessionKeys = [
          'token',
          'userid',
          'usertype',
          'username',
          'permissionid',
          'firstName',
          'lastName',
          'email',
          'mobile',
          'pathImage',
          'employeeID',
          'MenuId',
          'isSuperAdmin',
          'accessibleCompanies',
          'roleId',
          'isUserChat',
          '_fusion_sa_navigating_from_login',
          '_fusion_sa_navigating_back',
          'token_Snippet',
          'username_Snippet',
          'email_Snippet',
          'isSnippet',
        ];
        for (const key of sessionKeys) sessionStorage.removeItem(key);
        store.commit('logout');
      },
      _isSuperAdminRoute() {
        // SuperAdmin sessions expire like all others — always redirect to /login on 401
        return false;
      },
      // เรียกใน .then ของ helper ที่ส่ง Bearer token ก่อนส่ง response ต่อให้ callback (ดู TOKEN_EXPIRED_EVENT)
      // stream helper ได้ body เป็น string — parse เฉพาะ string สั้นที่ขึ้นต้นด้วย { (คำตอบแชทยาว ๆ ไม่ต้อง parse)
      _notifyIfTokenExpired(data) {
        let message = data?.message;
        if (typeof data === 'string' && data.length < 200 && data.trimStart().startsWith('{')) {
          try {
            message = JSON.parse(data).message;
          } catch {
            // ไม่ใช่ JSON — ไม่ใช่ response token หมดอายุ
          }
        }
        if (message === TOKEN_EXPIRED_MESSAGE) {
          globalThis.dispatchEvent(new CustomEvent(TOKEN_EXPIRED_EVENT));
        }
      },
      _isCrossDomainApi() {
        // snippet (เช่น *.azurestaticapps.net) เรียก API คนละ domain (เช่น *.azurewebsites.net)
        // → cookie auth (SameSite=Lax) ข้าม domain ไม่ได้ → 401 เป็นเรื่องปกติของบริบทนี้
        // FullStream ใช้ Bearer token (fs2_access_token) แทน cookie อยู่แล้ว
        try {
          const apiHost = new URL(this.urlApi, globalThis.location.origin).host;
          return !!apiHost && apiHost !== globalThis.location.host;
        } catch {
          return false;
        }
      },
      _isPublicAnonymousRoute() {
        // คืน true = "อย่า force redirect ไป /login เมื่อ 401"
        // หน้า widget หรือหน้าใด ๆ ใน iframe: ห้ามพาไป /login เด็ดขาด — ใน iframe ข้าม site JS ลบ guard cookies
        // แบบ None ของ portal ไม่ได้ /login จึงเห็น authenticated=true แล้วดันไป /dashboard → 401 → วนไม่รู้จบ
        // (incident 2026-09-23, doc 32) หน้าแชทจัดการ 401 เอง (ตกเป็น anonymous)
        if (isSnippetContext()) {
          // หน้าที่เปิดใน workspace ของหน้าบอท (iframe cf-embed) — บอกหน้าแม่ให้ปิด workspace แล้วพาไป login เอง
          // (เรียกจาก branch 401 เท่านั้น · หน้าอื่น/widget ไม่ส่งอะไร)
          notifyParent(EMBED_EVENTS.sessionExpired);
          return true;
        }
        // cross-domain: cookie auth ใช้ไม่ได้โดยธรรมชาติ — 401 ปกติ (กัน F5 หลุดบน snippet คนละ domain)
        if (this._isCrossDomainApi()) return true;
        // "/" รวมไว้ด้วยเพราะ Vue Router redirect '/' → '/login' เป็น async —
        // ระหว่าง redirect resolve, component อาจ mount บน '/' และยิง API ก่อน → 401 ไม่ควร force redirect ซ้ำ
        const publicPaths = ['/', '/login', '/register', '/register-success', '/forgot-password'];
        return publicPaths.includes(router.currentRoute.value?.path);
      },
      Logout() {
        this.apiPostAuth(
          'api/v0/Login/signout',
          null,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              this.ClearUserInfo();
              globalThis.location.replace('/login');
            }
          },
          (errorResource) => {
            console.error(errorResource);
            this.ClearUserInfo();
            globalThis.location.replace('/login');
          }
        );
      },
      LogoutNoRedirect(token, callback, error) {
        // FullStream V2 logout — ไม่ redirect (page เป็น standalone), backend signout cookie scheme
        // ส่ง Bearer token (auth_token) ให้ backend revoke session ทุก context ของ user (single-logout)
        this.apiPost('api/v0/Login/FullStreamLogout', { auth_token: token || null }, callback, error);
      },
      ChatLogout() {
        for (const cookieName of Object.keys(Cookies.get())) {
          if (cookieName != 'rememberMe') {
            Cookies.remove(cookieName);
          }
        }
      },
      GetUserInfo() {
        let Usid = Cookies.get('UserId');
        let Codeid = Cookies.get('UserCode');
        let UserFullName = Cookies.get('UserName');

        let temp = Number.parseInt(Usid);
        return {
          UserId: temp,
          UserCode: Codeid,
          UserName: UserFullName,
        };
      },
      images: {
        logout: logoutImage,
      },
      apiPostLogin(URL, request, callback, error) {
        const instance = axios.create({
          timeout: 500000,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .post(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              // Business error (status !== 0) หรือไม่มี payload → ส่ง ResponseModel เข้า error callback
              // (เช่น username/password ผิด, COMPANY_DISABLED) — กัน TypeError ตอนอ่าน data.data.userId เมื่อ data เป็น null
              if (!response.data || response.data.status !== 0 || !response.data.data) {
                return error(response.data || response);
              }
              // guard cookies ของ portal: Lax เหมือนที่ backend (PortalSessionCookies) ออก — iframe ข้าม site อ่านไม่เห็น
              const cookieOpts = portalCookieOpts();
              Cookies.set('userId', response.data.data.userId, cookieOpts);
              Cookies.set('username', response.data.data.username, cookieOpts);
              Cookies.set('fullName', response.data.data.fullName, cookieOpts);
              Cookies.set('roleId', response.data.data.roleId, cookieOpts);
              Cookies.set('authenticated', true, cookieOpts);
              store.state.status = 'success';
              store.state.token = response.data.data.token;
              store.state.userid = response.data.data.userId;
              store.state.username = response.data.data.username;
              store.state.user = response.data.data;
              store.state.fullName = response.data.data.fullName;
              store.state.roleId = response.data.data.roleId;
              store.state.isLoggedIn = true;
              localStorage.setItem('flag', 'us');
              localStorage.setItem('locale', 'th');
              store.state.languages = 'th';
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            return error(error_);
          });
      },
      apiPostLoginChat(URL, request, callback, error) {
        const instance = axios.create({
          timeout: 500000,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .post(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              for (const cookieName of Object.keys(Cookies.get())) {
                if (cookieName != 'rememberMe') {
                  Cookies.remove(cookieName);
                }
              }
              localStorage.setItem('locale', 'th');
              store.state.languages = 'th';
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            return error(error_);
          });
      },

      apiPostAuth(URL, request, callback, error) {
        const instance = axios.create({
          withCredentials: true,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .post(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            console.error('Unexpected Status:', response.status); // กรณี status ไม่ใช่ 200
            return error(response);
          })
          .catch((error_) => {
            console.error('API Error:', error_.response); // ข้อผิดพลาดจาก API
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            //else if(response.response.status === 400)
            //{
            //  console.log("Call 400");
            //  router.push('/login');
            //}
            else {
              console.log('เกิดข้อผิดพลาด: Something went wrong!');
              return error(error_);
            }
          });
      },
      apiPostImageAuth(URL, request, callback, error, Token) {
        const headers = {
          Accept: 'application/json',
          'Content-Type': 'application/json-patch+json; charset=utf-8',
        };

        if (Token) {
          headers.Authorization = 'Bearer ' + Token;
        }

        const instance = axios.create({
          headers: headers,
          responseType: 'blob',
        });
        instance
          .post(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              this._notifyIfTokenExpired(response.data);
              return callback(response.data);
            }
            console.error('Unexpected Status:', response.status); // กรณี status ไม่ใช่ 200
            return error(response);
          })
          .catch((error_) => {
            console.error('API Error:', error_.response); // ข้อผิดพลาดจาก API
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            //else if(response.response.status === 400)
            //{
            //  console.log("Call 400");
            //  router.push('/login');
            //}
            else {
              console.log('เกิดข้อผิดพลาด: Something went wrong!');
              return error(error_);
            }
          });
      },

      apiMultiPostAuth(URL, request, callback, error) {
        let formData = new FormData();
        formData.append('request', request);
        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        });
        instance
          .post(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },

      apiPostAuthNoAlert_Config(URL, request, Config, callback, error) {
        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .post(this.urlApi + URL, request, Config)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            return error(error_);
          });
      },
      apiPostAuthNoAlert(URL, request, callback, error) {
        const instance = axios.create({
          withCredentials: true, // แนบ auth cookie เหมือน apiPostAuth/apiGetAuth — ขาดอันนี้ cookie ไม่ถูกส่ง → 401 ตลอด
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .post(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            return error(error_);
          });
      },
      apiPostAuthNoAlertToken(URL, request, callback, error, Token) {
        const instance = axios.create({
          headers: {
            Authorization: 'Bearer ' + Token,
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .post(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              this._notifyIfTokenExpired(response.data);
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 403) {
              // snippet resolve ไม่ได้ (invalid_snippet) → ไม่เด้ง /AccessDenied ทั้งแอป
              // ปล่อยให้ caller (loadTopics/loadHistory) แสดง notice + refId เองแบบ inline
              const errCode = error_.response.data?.errorCode;
              if (errCode !== 'invalid_snippet') {
                router.push('/AccessDenied');
                console.log('Access Denied');
              }
              return error(error_);
            }
            return error(error_);
          });
      },
      apiPutAuth(URL, request, callback, error) {
        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .put(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },
      apiPostTypeFileAuth(URL, request, callback, error) {
        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            // 'Content-Type': 'application/json-patch+json; charset=utf-8'
          },
          responseType: 'blob',
        });
        instance
          .post(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },
      apiPostTypeFileToken(URL, request, callback, error, Token) {
        const instance = axios.create({
          headers: {
            Authorization: 'Bearer ' + Token,
            Accept: 'application/json',
            //'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
          responseType: 'blob',
        });
        instance
          .post(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              this._notifyIfTokenExpired(response.data);
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },
      apiGetTypeFileAuth(URL, callback, error) {
        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            // 'Content-Type': 'application/json-patch+json; charset=utf-8'
          },
          responseType: 'blob',
        });
        instance
          .get(this.urlApi + URL)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },
      apiPostTypeFileNoResponseAuth(URL, request, callback, error) {
        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            // 'Content-Type': 'application/json-patch+json; charset=utf-8'
          },
        });
        instance
          .post(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },
      apiPostTypeFileMultipart(URL, request, callback, error) {
        const jsonBody = {
          vectorDBName: request.vectordbName,
          modelType: request.modelType,
          deleteAll: request.deleteAll,
          customCharacter: request.customCharacter,
          topVDB: request.topVDB,
          cosine: request.cosine,
          topicID: request.TopicID,
          userChannel: request.USER_CHANNEL,
          groupReplaceID: request.groupReplaceID,
          detectLanguage: request.DetectLanguage,
          fileId: request.fileId,
          fileName: request.searchVectorDBName2,
          maxLimitedCharacter: request.MaxLimitedCharacter,
        };
        request.entity.append('jsonBody', JSON.stringify(jsonBody));

        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            // "Content-Type": "multipart/form-data"
          },
        });
        instance
          .post(this.urlApi + URL, request.entity)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },
      apiPostTypeFileNoResponseToken(URL, request, callback, error, Token) {
        const instance = axios.create({
          headers: {
            Authorization: 'Bearer ' + Token,
            Accept: 'application/json',
            // 'Content-Type': 'application/json-patch+json; charset=utf-8'
          },
        });
        instance
          .post(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              this._notifyIfTokenExpired(response.data);
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },
      apiPostToken(URL, request, callback, error, Token) {
        const instance = axios.create({
          headers: {
            Authorization: 'Bearer ' + Token,
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .post(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              this._notifyIfTokenExpired(response.data);
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            } else if (error_.response != undefined && error_.response.status === 403) {
              router.push('/AccessDenied');
              console.log('Access Denied');
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },
      apiGetBlobAuth(URL, callback, error) {
        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
          responseType: 'blob',
        });
        instance
          .get(this.urlApi + URL)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },

      apiGetBlobAuthData(URL, callback, error) {
        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
          responseType: 'blob',
        });
        instance
          .get(this.urlApi + URL)
          .then((response) => {
            if (response.status === 200) {
              return callback(response);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },
      apiGetAuth(URL, callback, error) {
        const instance = axios.create({
          withCredentials: true,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .get(this.urlApi + URL)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },
      apiGetAuthNoAlert(URL, callback, error) {
        const instance = axios.create({
          withCredentials: true,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .get(this.urlApi + URL)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            return error(error_);
          });
      },
      apiGetToken(URL, callback, error, Token) {
        const instance = axios.create({
          headers: {
            Authorization: 'Bearer ' + Token,
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .get(this.urlApi + URL)
          .then((response) => {
            if (response.status === 200) {
              this._notifyIfTokenExpired(response.data);
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            } else if (error_.response != undefined && error_.response.status === 403) {
              router.push('/AccessDenied');
              console.log('Access Denied');
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
          });
      },
      apiDeleteAuth(URL, callback, error) {
        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .delete(this.urlApi + URL)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },
      apiDeleteToken(URL, callback, error, Token) {
        const instance = axios.create({
          headers: {
            Authorization: 'Bearer ' + Token,
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .delete(this.urlApi + URL)
          .then((response) => {
            if (response.status === 200) {
              this._notifyIfTokenExpired(response.data);
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },
      apiDeleteFileAuth(URL, fileIds, callback, error) {
        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .delete(this.urlApi + URL, {
            data: fileIds,
          })
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            if (error_.response != undefined && error_.response.status === 401) {
              if (this._isSuperAdminRoute()) {
                return error(error_);
              }
              if (this._isPublicAnonymousRoute()) {
                return error(error_);
              }
              this.ClearUserInfo();
              router.push('/login');
              Swal.fire({
                iconHtml: '<img style="width: 100%;" alt="edit" src="' + this.images.logout + '" v-bind="attrs" v-on="on">',
                title: 'กรุณา Log in ใหม่',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  icon: 'no-border',
                },
              });
              return error(error_);
            }
            console.log('เกิดข้อผิดพลาด: Something went wrong!');
            return error(error_);
          });
      },
      apiPostText_Stream(URL, Config, request, callback, error) {
        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .post(this.urlApi + URL, request, Config)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            return error(error_);
          });
      },
      apiPostTokenText_Stream(URL, Config, request, callback, error, Token) {
        const instance = axios.create({
          headers: {
            Authorization: 'Bearer ' + Token,
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .post(this.urlApi + URL, request, Config)
          .then((response) => {
            if (response.status === 200) {
              this._notifyIfTokenExpired(response.data);
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            return error(error_);
          });
      },

      apiPostImportFileAuth(URL, request, Config, callback, error) {
        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        });
        instance
          .post(this.urlApi + URL, request, Config)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            return error(error_);
          });
      },

      apiPostImportFile(URL, request, callback, error) {
        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            'Content-Type': 'form-data',
          },
        });
        instance
          .post(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            return error(error_);
          });
      },

      // Anonymous GET — no credentials required (public endpoints)
      apiGet(URL, callback, error) {
        const instance = axios.create({
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .get(this.urlApi + URL)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            return error(error_);
          });
      },

      // Anonymous POST — no credentials required (public endpoints e.g. Terms/Accept)
      apiPost(URL, request, callback, error) {
        const instance = axios.create({
          withCredentials: true,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json-patch+json; charset=utf-8',
          },
        });
        instance
          .post(this.urlApi + URL, request)
          .then((response) => {
            if (response.status === 200) {
              return callback(response.data);
            }
            return error(response);
          })
          .catch((error_) => {
            return error(error_);
          });
      },
    };
  },
};

export default APICall;
